import {
  Brain, Code2, Globe, Smartphone, Cloud, Boxes, Users2, Contact2, Radio, Workflow,
  HeartPulse, GraduationCap, Landmark, Building2, ShoppingBag, Factory, Truck, Shield,
  UtensilsCrossed, Wheat, Search, PenTool, Rocket, TestTube2, GitBranch, LifeBuoy, Layers,
  BarChart3, Palette, Cpu, Wrench, ShieldCheck, Compass, Lightbulb, CircuitBoard, BriefcaseBusiness
} from "lucide-react";
import BLOGS_DATA from "./blogsData.json";


/* ---------------- Navigation ---------------- */
export const NAV = [
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Solutions", to: "/solutions" },
  { label: "Industries", to: "/industries" },
  { label: "Our Work", to: "/portfolio" },
  { label: "Technologies", to: "/#technologies" },
  { label: "Blog", to: "/blog" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
];

/* ---------------- Stats ---------------- */
export const STATS = [
  { value: 50, suffix: "+", label: "Projects" },
  { value: 20, suffix: "+", label: "Clients" },
  { value: 10, suffix: "+", label: "Technologies" },
];


/* ---------------- Services (SEO pages) ---------------- */
export const SERVICES = [
  {
    slug: "software-development",
    icon: Code2,
    title: "Software Development",
    short: "Web, mobile & bespoke enterprise platforms.",
    desc: "High-performance web applications, native & cross-platform mobile apps (Flutter / React Native), and tailored systems built around your exact business process.",
    seoTitle: "Custom Software Development Company | Enterprise Software Engineering | NeuraVolt",
    seoDesc: "Partner with NeuraVolt for custom software development, bespoke enterprise platforms, scalable microservices, and mobile apps. Engineering from Gwalior for global scale.",
    seoKeywords: "custom software development company, custom software development, software development company, enterprise software development, custom software engineering, software development company in Gwalior, bespoke software development company, NeuraVolt",
    localProof: "Engineered by NeuraVolt with active enterprise deployments across India, Central India, and global markets.",
    features: ["Modern Next.js & React web platforms and portals", "Native-feel iOS & Android mobile apps (Flutter / React Native)", "Bespoke business operating systems with zero licensing bloat", "Scalable REST & GraphQL microservice backends", "Cloud-native database design & event architectures", "Continuous CI/CD deployment & technical maintenance"]
  },
  {
    slug: "custom-software",
    canonicalSlug: "software-development",
    icon: Code2,
    title: "Custom Software",
    short: "Bespoke software engineered around your exact workflow.",
    desc: "Tailored platforms built to your process with no bloated licences and no compromises. Scalable architecture from day one.",
    seoTitle: "Custom Software Development Company | Enterprise Software Engineering | NeuraVolt",
    seoDesc: "Partner with NeuraVolt for custom software development, bespoke enterprise platforms, scalable microservices, and mobile apps. Engineering from Gwalior for global scale.",
    seoKeywords: "custom software development company, custom software development, software development company, enterprise software development, custom software engineering",
    features: ["Architecture & discovery", "Full-stack engineering", "Systems integration", "Legacy modernization", "API platforms", "Ongoing support"]
  },
  {
    slug: "ai-solutions",
    icon: Brain,
    title: "AI Solutions",
    short: "Cognitive models, LLMs & autonomous workflows.",
    desc: "Production-ready artificial intelligence: custom LLM fine-tuning, enterprise RAG knowledge engines, and autonomous multi-agent pipelines creating measurable business leverage.",
    seoTitle: "AI Development Company | Autonomous AI Agents & Custom LLMs | NeuraVolt",
    seoDesc: "NeuraVolt is a premier AI development company engineering production LLM fine-tuning, autonomous multi-agent pipelines, enterprise RAG engines, and cognitive automation.",
    seoKeywords: "ai development company, ai software development company, ai agent development services, ai development services, ai app development, business process automation services, ai development company in gwalior, enterprise AI solutions",
    localProof: "Production AI systems and LLM workflows engineered at our Gwalior deep-tech center for clients in India, UAE, and the US.",
    features: ["Custom LLM fine-tuning & domain-adapted intelligence", "Enterprise RAG with vectorized semantic knowledge search", "Autonomous agent orchestration for business workflows", "Real-time computer vision & predictive MLOps pipelines", "AI guardrails, safety & prompt engineering", "Continuous model evaluation & retraining workflows"]
  },
  {
    slug: "ai-development",
    canonicalSlug: "ai-solutions",
    icon: Brain,
    title: "AI Development",
    short: "Production AI systems that reason, predict and automate.",
    desc: "We design and ship production grade AI from custom models to LLM copilots that create measurable leverage across your business.",
    seoTitle: "AI Development Company | Autonomous AI Agents & Custom LLMs | NeuraVolt",
    seoDesc: "NeuraVolt is a premier AI development company engineering production LLM fine-tuning, autonomous multi-agent pipelines, enterprise RAG engines, and cognitive automation.",
    seoKeywords: "ai development company, ai software development company, ai agent development services, ai development services, ai app development",
    features: ["Custom model development", "LLM & RAG systems", "Computer vision", "Predictive analytics", "MLOps & monitoring", "Responsible AI"]
  },
  {
    slug: "web-development",
    icon: Globe,
    title: "Web Development",
    short: "Blazing-fast, scalable web platforms and portals.",
    desc: "High-performance websites and web apps with pixel-perfect UX, built on modern frameworks and optimized for conversion.",
    seoTitle: "Web Application Development Company | High-Performance Web Platforms | NeuraVolt",
    seoDesc: "High-performance web application development company building conversion-focused web platforms, client portals, and cloud SaaS backends from Gwalior to global markets.",
    seoKeywords: "web application development company, web development company, custom web application development, web development company in gwalior, website development company in gwalior, enterprise web portals",
    localProof: "Building high-performance portals and web applications for regional commercial clients in Gwalior and national digital brands.",
    features: ["Marketing sites", "Web applications", "E-commerce", "Portals & dashboards", "Headless CMS", "Core Web Vitals"]
  },
  {
    slug: "mobile-apps",
    icon: Smartphone,
    title: "Mobile Apps",
    short: "Native-feel iOS & Android from one codebase.",
    desc: "Cross-platform mobile apps that feel native, ship fast, and delight users powered by Flutter and React Native.",
    seoTitle: "Mobile App Development Company | Flutter & React Native Solutions | NeuraVolt",
    seoDesc: "Cross-platform mobile app development company engineering high-performance iOS and Android apps using Flutter and React Native. Offline-first architectures and GPS telemetry.",
    seoKeywords: "mobile app development company, mobile app developer services, mobile app development company in gwalior, android app development company in gwalior, ios app development company in gwalior, custom mobile application development",
    localProof: "Engineered scalable mobile apps such as FoxRun (telemetry) and MyCityDekho (hyperlocal discovery) from our Gwalior center.",
    features: ["iOS & Android", "Cross platform", "Offline first", "Push & realtime", "App store launch", "Analytics"]
  },
  {
    slug: "cloud-infrastructure",
    icon: Cloud,
    title: "Cloud Infrastructure",
    short: "Dedicated extreme speed bare metal on neuravolt.cloud.",
    brandLogo: "/brand/cloudservices-logo.png",
    externalUrl: "https://neuravolt.cloud",
    desc: "Mission-critical dedicated cloud hosting on neuravolt.cloud. High-security bare-metal clusters, ultra-low latency routing, and guaranteed zero downtime.",
    seoTitle: "Cloud DevOps Consulting & Bare-Metal Hosting Services | NeuraVolt Cloud",
    seoDesc: "Resilient cloud DevOps consulting, Kubernetes container orchestration, CI/CD pipelines, and dedicated high-speed bare-metal hosting on neuravolt.cloud with zero downtime.",
    seoKeywords: "cloud devops consulting, kubernetes consulting services, cloud migration consulting, cloud infrastructure, cloud services company in gwalior, neuravolt.cloud bare metal",
    features: ["Dedicated bare metal & container clusters on neuravolt.cloud", "Ultra-low latency global routing & edge delivery", "Zero-downtime high-availability architecture", "Automated multi-cloud IaC (AWS, GCP, Azure)", "24/7 proactive monitoring & disaster recovery", "Dedicated IP allocation & custom DNS routing"]
  },
  {
    slug: "cloud-devops",
    canonicalSlug: "cloud-infrastructure",
    icon: Cloud,
    title: "Cloud & DevOps",
    short: "Resilient cloud infra and zero-downtime delivery.",
    desc: "Cloud architecture, CI/CD, IaC and observability that ship faster and scale without surprises.",
    seoTitle: "Cloud DevOps Consulting & Bare-Metal Hosting Services | NeuraVolt Cloud",
    seoDesc: "Resilient cloud DevOps consulting, Kubernetes container orchestration, CI/CD pipelines, and dedicated high-speed bare-metal hosting on neuravolt.cloud with zero downtime.",
    seoKeywords: "cloud devops consulting, kubernetes consulting services, cloud migration consulting, cloud infrastructure",
    features: ["AWS / GCP / Azure", "CI/CD pipelines", "Infrastructure as Code", "Kubernetes", "Monitoring", "Cost optimization"]
  },
  {
    slug: "consultancy",
    icon: Lightbulb,
    title: "Technology Consultancy",
    short: "Enterprise roadmap & fractional CTO leadership.",
    desc: "Strategic engineering advisory for fast-growing ventures and established enterprises. We architect scalable roadmaps, de-risk cloud migrations, and guide AI transformation.",
    seoTitle: "Technology Consulting Services & Fractional CTO Leadership | NeuraVolt",
    seoDesc: "Enterprise technology consulting services, systems architecture audits, fractional CTO advisory, and legacy modernization roadmaps for growing companies and enterprises.",
    seoKeywords: "technology consulting services, technology consulting company, tech consulting firm, fractional CTO, digital transformation company in gwalior, technology advisory services",
    features: ["Fractional CTO leadership & engineering team scaling", "Systems architecture audits & technical due diligence", "Cloud economics & infrastructure cost optimization", "Legacy modernization & cloud migration roadmaps", "AI transformation strategy & tech stack selection", "Security, compliance & risk assessment"]
  },
  {
    slug: "erp",
    icon: Boxes,
    title: "ERP",
    short: "Unified operations across every department.",
    desc: "Custom ERP that connects finance, inventory, procurement and operations into a single source of truth.",
    seoTitle: "Custom ERP Software Development Company | Unified Enterprise Platforms | NeuraVolt",
    seoDesc: "Bespoke custom ERP software development company engineering unified financial, inventory, procurement, and billing platforms with zero recurring licensing overhead.",
    seoKeywords: "custom erp software development, custom erp software, erp software development company, erp software company in gwalior, bespoke erp system, enterprise ERP solutions",
    features: ["Finance & accounting", "Inventory", "Procurement", "Reporting", "Role based access", "Integrations"]
  },
  {
    slug: "hrms",
    icon: Users2,
    title: "HRMS",
    short: "People operations, automated end to end.",
    desc: "Human resource management systems for attendance, payroll, performance and the full employee lifecycle.",
    seoTitle: "Custom HRMS Software Development & People Operations Automation | NeuraVolt",
    seoDesc: "End-to-end human resource management systems (HRMS) covering biometric attendance, automated payroll, performance telemetry, and Indian compliance automation.",
    seoKeywords: "hrms software development, custom hrms software, payroll automation software, employee attendance platform, HRMS development India",
    features: ["Attendance & leave", "Payroll", "Performance", "Recruitment", "Self-service", "Compliance"]
  },
  {
    slug: "crm",
    icon: Contact2,
    title: "CRM",
    short: "Close more, retain longer, know your customer.",
    desc: "Sales and customer relationship platforms that unify pipeline, marketing and support.",
    seoTitle: "Custom CRM Software Development | Enterprise Pipeline & Lead Automation | NeuraVolt",
    seoDesc: "Tailored CRM development unifying sales pipelines, lead scoring, omnichannel communication, and customer analytics into one seamless team workflow.",
    seoKeywords: "crm software development, custom crm software, sales pipeline automation, enterprise crm solutions, customer relationship management software",
    features: ["Pipeline management", "Lead scoring", "Automation", "Analytics", "Omnichannel", "Integrations"]
  },
  {
    slug: "iot-solutions",
    icon: Cpu,
    title: "IoT & Automation",
    short: "Connected devices, edge & remote control.",
    desc: "End-to-end IoT ecosystems linking industrial sensors, edge microcontrollers, and live telemetry dashboards with automated operational actions.",
    seoTitle: "IoT Development Company | Industrial Telemetry & Hardware Systems | NeuraVolt",
    seoDesc: "End-to-end IoT development company linking industrial sensors, edge microcontrollers, and live telemetry dashboards with automated operational control.",
    seoKeywords: "iot development company, industrial iot solutions, agriculture IoT solutions, edge computing telemetry, hardware sensor integration, NeuraVolt IoT",
    localProof: "Field-tested agricultural IoT and telemetry systems (Krishi JalSevak) deployed in Madhya Pradesh rural clusters.",
    features: ["Sensor hardware connectivity & edge microcontroller processing", "Real-time telemetry streaming, alerting & anomalies detection", "Remote device fleet management & secure OTA updates", "Industrial workflow automation & predictive diagnostics", "Edge computing data aggregation & filtering", "Encrypted device-to-cloud MQTT & TLS telemetry"]
  },
  {
    slug: "iot",
    canonicalSlug: "iot-solutions",
    icon: Cpu,
    title: "IoT",
    short: "Connected devices with real-time intelligence.",
    desc: "End-to-end IoT ecosystems (devices, edge, telemetry, and dashboards) for smart data driven operations.",
    seoTitle: "IoT Development Company | Industrial Telemetry & Hardware Systems | NeuraVolt",
    seoDesc: "End-to-end IoT development company linking industrial sensors, edge microcontrollers, and live telemetry dashboards with automated operational control.",
    seoKeywords: "iot development company, industrial iot solutions, agriculture IoT solutions, edge computing telemetry",
    features: ["Device connectivity", "Edge computing", "Realtime telemetry", "Dashboards", "Alerts", "Security"]
  },
  {
    slug: "ai-automation",
    icon: Workflow,
    title: "AI Automation",
    short: "Eliminate busywork with intelligent workflows.",
    desc: "Automate repetitive operations with AI agents and workflow engines that free your team for high-value work.",
    seoTitle: "AI Workflow Automation Services & Intelligent Agents | NeuraVolt",
    seoDesc: "Automate repetitive business operations with custom AI agents, automated document extraction, intelligent triage, and human-in-the-loop workflows.",
    seoKeywords: "ai automation services, business process automation services, ai agent workflows, document processing AI, enterprise intelligent automation",
    features: ["Agentic workflows", "Document processing", "Customer support AI", "Data extraction", "API orchestration", "Human-in-the-loop"]
  },
  {
    slug: "managed-services",
    icon: LifeBuoy,
    title: "Managed Services & Maintenance",
    short: "Proactive 24/7 monitoring, security updates, and continuous optimization.",
    desc: "We proactively monitor, maintain, secure, and continuously improve your applications and infrastructure with AMC, performance optimization, security updates, backups, and technical support.",
    seoTitle: "Managed IT Services & 24/7 Software Maintenance Support | NeuraVolt",
    seoDesc: "Proactive 24/7 monitoring, security audits, zero-downtime patch updates, automated backups, and SLA-guaranteed application maintenance support.",
    seoKeywords: "managed IT services, software maintenance services, 24/7 application support, AMC software development, cloud infrastructure maintenance",
    features: ["24/7 Monitoring & Alerting", "AMC & SLA Guarantees", "Zero-Downtime Deployments", "Security Patches & Audits", "Automated Backups & Disaster Recovery", "Continuous Performance Optimization"]
  },
  {
    slug: "cybersecurity",
    icon: ShieldCheck,
    title: "Cybersecurity",
    short: "Autonomous threat defense & self-healing SRE.",
    brandLogo: "/brand/guardian-logo.png",
    desc: "Zero-trust network security, autonomous vulnerability mitigation, and continuous self-healing site reliability engineering powered by Guardian.",
    seoTitle: "Cybersecurity Consulting & Server Security Monitoring | NeuraVolt Guardian",
    seoDesc: "Zero-trust network defense, proactive vulnerability mitigation, and autonomous self-healing SRE infrastructure services engineered by NeuraVolt.",
    seoKeywords: "cybersecurity consulting services, cybersecurity company in Gwalior, server security monitoring software, autonomous SRE, zero trust network security",
    features: ["Neuravolt Guardian self-healing SRE", "Zero-trust network security & policy audits", "Continuous vulnerability scanning & threat defense", "Container isolation & automated memory leak healing", "DDoS mitigation & SSL/TLS certificate automation", "Regulatory compliance (DPDP, GDPR, SOC2 readiness)"]
  }
];

/* ── Unified Offerings: Services & Products Engineered for Scale ── */
export const ALL_OFFERINGS = [
  {
    id: "software-development",
    slug: "software-development",
    type: "service",
    title: "Software Development",
    icon: Code2,
    badge: "Core Engineering",
    desc: "Custom web applications, cross-platform mobile apps, and high-concurrency microservices built around your business logic.",
    tags: ["Web & Portals", "Mobile (iOS/Android)", "Custom Systems"],
    link: "/services/software-development",
  },
  {
    id: "ai-solutions",
    slug: "ai-solutions",
    type: "service",
    title: "AI Solutions",
    icon: Brain,
    badge: "Intelligence Core",
    desc: "Autonomous multi-agent orchestration, enterprise RAG knowledge engines, and fine-tuned domain LLMs.",
    tags: ["Agentic AI", "Enterprise RAG", "Fine-Tuned LLMs"],
    link: "/services/ai-solutions",
  },
  {
    id: "iot-solutions",
    slug: "iot-solutions",
    type: "service",
    title: "IoT & Automation",
    icon: Cpu,
    badge: "Hardware & Edge",
    desc: "Industrial sensor telemetry, edge computing firmware, remote fleet monitoring, and automated controls.",
    tags: ["Edge Telemetry", "Industrial Sensors", "Fleet Control"],
    link: "/services/iot-solutions",
  },
  {
    id: "cybersecurity",
    slug: "cybersecurity",
    type: "service",
    title: "Cybersecurity",
    icon: ShieldCheck,
    brandLogo: "/brand/guardian-logo.png",
    brandAlt: "Guardian",
    badge: "Defense & SRE",
    desc: "Zero-trust network defense, proactive vulnerability mitigation, and autonomous self-healing SRE infrastructure.",
    tags: ["Zero Trust", "Autonomous SRE", "Threat Defense"],
    link: "/guardian",
  },
  {
    id: "cloud-infrastructure",
    slug: "cloud-infrastructure",
    type: "service",
    title: "Cloud Infrastructure",
    icon: Cloud,
    brandLogo: "/brand/cloudservices-logo.png",
    brandAlt: "Neuravolt Cloud Services",
    badge: "neuravolt.cloud",
    desc: "Dedicated bare-metal clusters and container infrastructure operating on neuravolt.cloud with zero downtime.",
    tags: ["neuravolt.cloud", "Bare Metal", "Zero Downtime"],
    link: "/services/cloud-infrastructure",
  },
  {
    id: "consultancy",
    slug: "consultancy",
    type: "service",
    title: "Technology Consultancy",
    icon: Lightbulb,
    badge: "Advisory",
    desc: "Fractional CTO advisory, systems architecture discovery, legacy migration roadmaps, and cloud economics.",
    tags: ["CTO Advisory", "Architecture Audits", "Modernization"],
    link: "/services/consultancy",
  },
  {
    id: "norbit",
    slug: "norbit",
    type: "product",
    title: "nORBIT Enterprise OS",
    icon: Cpu,
    brandLogo: "/brand/norbit-logo.png",
    brandAlt: "nORBIT",
    badge: "Enterprise OS",
    desc: "Autonomous enterprise operating system consolidating ERP, CRM, HRMS, and automated accounting into one sovereign platform.",
    tags: ["Enterprise OS", "9 Core Pillars", "Zero SaaS Bloat"],
    link: "/norbit",
    actionLabel: "Explore nORBIT",
  },
  {
    id: "reach",
    slug: "reach",
    type: "product",
    title: "Reach Growth Engine",
    icon: Rocket,
    brandLogo: "/brand/reach-logo.png",
    brandAlt: "Reach",
    badge: "Growth Engine",
    desc: "Automated customer acquisition and audience conversion engine operating on Neuravolt dedicated high-speed edge infrastructure.",
    tags: ["Audience Funnels", "Live Telemetry", "Sub-20ms SLA"],
    link: "/reach",
    actionLabel: "Explore Reach",
  },
];

/* ---------------- Solutions (trending services) ---------------- */
export const SOLUTIONS = [
  { title: "Web Development",   icon: Globe,      tag: "Most Popular", desc: "Blazing-fast web apps, portals & marketing sites built on modern frameworks, optimised for Core Web Vitals and conversion." },
  { title: "Mobile Apps",       icon: Smartphone, tag: "Top Demand",   desc: "Cross-platform iOS & Android apps that feel native, ship fast, and delight users with Flutter & React Native." },
  { title: "AI Development",    icon: Brain,      tag: "Trending",     desc: "Custom LLMs, RAG systems, computer vision and MLOps to deliver production AI that creates measurable leverage." },
  { title: "Cloud & DevOps",    icon: Cloud,      tag: "Infrastructure", desc: "AWS / GCP / Azure architecture, CI/CD pipelines, Kubernetes and zero-downtime delivery at any scale." },
  { title: "SaaS Development",  icon: Layers,     tag: "High Growth",  desc: "Multi-tenant SaaS platforms built for scale from MVP to enterprise, with billing, auth and analytics baked in." },
  { title: "Custom Software",   icon: Code2,      tag: "Core",         desc: "Bespoke platforms engineered around your exact workflow with scalable architecture from day one." },
  { title: "UI / UX Design",    icon: Palette,    tag: "Creative",     desc: "Research driven design systems, wireframes and pixel-perfect interfaces that convert visitors into customers." },
  { title: "Data & Analytics",  icon: BarChart3,  tag: "Intelligence", desc: "Data pipelines, warehouses, BI dashboards and real-time visualisations that turn raw data into decisions." },
  { title: "AI Automation",     icon: Workflow,   tag: "Efficiency",   desc: "Intelligent workflow automation with AI agents, document processing, and integrations that eliminate busywork." },
];


/* ---------------- Industries ---------------- */
export const INDUSTRIES = [
  { icon: HeartPulse, name: "Healthcare" }, { icon: GraduationCap, name: "Education" },
  { icon: Landmark, name: "B2B Industry" }, { icon: Building2, name: "Real Estate" },
  { icon: ShoppingBag, name: "Retail" }, { icon: Factory, name: "Manufacturing" },
  { icon: Truck, name: "Logistics" }, { icon: Shield, name: "Government" },
  { icon: UtensilsCrossed, name: "Hospitality" }, { icon: Wheat, name: "Agriculture" },
  { icon: Palette, name: "Arts & Culture" },
];

/* ---------------- Portfolio Projects (Single Source of Truth) ---------------- */
export const PROJECTS = [
  {
    slug: "php2shell",
    title: "PHP2Shell",
    domain: "advisory.neuravolt.in/php2shell",
    live_url: "https://neuravolt.in/portfolio/php2shell",
    sector: "cybersecurity",
    cat: "Security",
    badge: "Threat Analysis & Advisory",
    tagline: "Runtime Vulnerability Analysis & Infrastructure Hardening",
    desc: "In-depth cybersecurity analysis of critical PHP runtime vulnerabilities, detailing memory corruption vectors, remote code execution risks, and multi-layered infrastructure defense strategies.",
    overview: "PHP2Shell is an executive-level threat analysis and defensive engineering case study exploring how critical vulnerabilities in foundational web technologies expose applications, server environments, and enterprise data. By examining low-level memory management and request-handling flaws within the PHP runtime, this study identifies actionable architectural frameworks organizations must adopt to prevent unauthenticated remote code execution and build resilient web systems.",
    challenge: "Modern web architectures rely on complex multi-tiered dependency chains spanning application code, language runtimes, web servers, and containerized cloud infrastructure. When a critical vulnerability exists within the underlying runtime engine rather than custom application logic, conventional access controls, authentication layers, and business rules fail to mitigate the risk. Organizations face acute hurdles identifying vulnerable deployments across legacy and production nodes, containing zero-day RCE vectors, maintaining 99.99% service availability during emergency remediation, and systematically investigating potential credential or data exposure.",
    solution: "NeuraVolt engineered a proactive, multi-layered defensive framework centered on holistic attack surface reduction: continuous asset discovery across microservices and containers, zero-trust process isolation to prevent runtime escapes, automated vendor patch deployment pipelines, and hardened web application firewall (WAF) rule sets. In parallel, comprehensive log telemetry, least-privilege service configurations, and standardized incident recovery playbooks were deployed to ensure rapid containment without business disruption.",
    results: [
      "Full attack surface visibility across runtime installations & containers",
      "Zero-trust process containment preventing unauthenticated RCE escape",
      "Automated vulnerability detection & live security telemetry alerts",
      "Standardized patch management playbook with zero unplanned downtime",
      "Least-privilege infrastructure segmentation & credential protection"
    ],
    metrics: [
      "CVSS 9.8 Critical Vulnerability Analyzed",
      "Zero-Downtime Patching & Runtime Isolation",
      "100% Asset Visibility & Hardened Defense"
    ],
    tech: ["Cybersecurity", "Threat Analysis", "PHP Runtime", "RCE Mitigation", "Zero Trust", "DevSecOps", "WAF / SIEM", "Container Security"],
    logo: "/brand/php2shell-logo.png",
    darkLogo: "/brand/php2shell-logo.png",
    img: "/brand/php2shell-preview.png",
    takeaway: "Strong web security depends on secure application code, maintained runtimes, least-privilege infrastructure access controls, and continuous proactive monitoring.",
    mitigationSteps: [
      { step: "01", name: "Identify", desc: "Maintain real-time inventory of all PHP runtimes, servers, containers, and third-party dependencies across production and legacy clusters." },
      { step: "02", name: "Assess", desc: "Evaluate exposure radius, prioritize public-facing entry points, and model unauthenticated execution paths." },
      { step: "03", name: "Update", desc: "Deploy upstream security patches through automated staging pipelines with zero operational disruption." },
      { step: "04", name: "Investigate", desc: "Perform deep forensic audit of web server access logs, runtime anomalies, and SIEM telemetry for indicators of compromise (IoCs)." },
      { step: "05", name: "Recover", desc: "Isolate compromised processes, restore verified clean baseline images, and rotate service account credentials." },
      { step: "06", name: "Prevent", desc: "Enforce repeatable vulnerability management SLAs, automated container vulnerability scanning, and immutable runtime policies." }
    ],
    keyFindings: [
      "Security extends beyond application code — low-level runtime flaws bypass application-layer authentication.",
      "Patch velocity is mission-critical — rapid automated updates eliminate exposure windows before weaponization.",
      "Full infrastructure visibility is indispensable — untracked shadow services represent the highest systemic risk.",
      "Proactive telemetry accelerates forensic triage — structured logging enables immediate anomaly identification.",
      "Defense-in-depth reduces business impact — container sandboxing limits lateral movement during an exploit."
    ]
  },
  {
    slug: "wordpress-security-hardening",
    title: "WordPress Website Recovery & Security Hardening",
    domain: "advisory.neuravolt.in/wordpress-security",
    live_url: "https://neuravolt.in/portfolio/wordpress-security-hardening",
    sector: "cybersecurity",
    cat: "Security",
    badge: "Cybersecurity & Website Maintenance",
    tagline: "Recover Compromised Websites, Restore Business Continuity & Build a Stronger Security Foundation",
    desc: "Structured incident response, forensic malware investigation, and security hardening for compromised WordPress websites to restore business continuity and eliminate recurring threats.",
    overview: "A website going down or being compromised can quickly become a serious problem for a business. Beyond losing access to the website, owners may face unexpected redirects, suspicious files, broken functionality, and concerns about the safety of their data.\n\nAt Neuravolt, we worked with clients whose WordPress websites had been affected by cyberattacks. Our focus was to recover their websites, restore normal functionality, and address security weaknesses that could leave them exposed to further attacks.\n\nThe objective was not simply to bring the websites back online, but to help clients regain control of their digital presence and improve the overall security of their websites.",
    challenge: "The affected websites were built on WordPress and relied on plugins, themes, and server-side components to deliver their functionality. WordPress websites can become vulnerable when software contains security flaws, plugins are outdated, or server configurations and access controls are not properly maintained.\n\nOur clients needed a reliable recovery process that addressed both the immediate website issues and the underlying security concerns. The main challenges included: investigating the condition of compromised WordPress websites, identifying suspicious files, unauthorized changes, and potential malware, restoring website functionality while preserving essential content and data, reviewing WordPress plugins, themes, and administrative access, and systematically reducing the risk of repeated security incidents.",
    solution: "NeuraVolt followed a structured five-stage recovery and defensive engineering framework to bring affected websites back into a stable and usable state: thorough website condition assessment, forensic malware and file integrity investigation, clean data and functional restoration, comprehensive WordPress environment review, and rigorous infrastructure hardening and post-recovery validation.",
    results: [
      "Website Recovery: Restored affected websites to a usable, secure state",
      "Functional Restoration: Brought essential pages and critical website features back online",
      "Security Improvements: Addressed identified weaknesses in the WordPress environment",
      "Access Control Review: Audited administrative access and account security policies",
      "Improved Maintainability: Established a stronger foundation for ongoing updates, backups, and monitoring"
    ],
    metrics: [
      "100% Core Functionality Restored",
      "Zero Data Loss During Recovery",
      "Proactive Defense & Hardening Enforced"
    ],
    tech: [
      "Cybersecurity",
      "Website Recovery",
      "Malware Investigation",
      "WordPress Security",
      "Infrastructure Hardening",
      "Access Control",
      "Database Integrity",
      "Backup Systems"
    ],
    logo: "/brand/wordpress-security-logo.png",
    darkLogo: "/brand/wordpress-security-logo.png",
    img: "/brand/wordpress-security-preview.png",
    takeaway: "At Neuravolt, we understand that a compromised website is more than a technical issue. It affects credibility, customer experience, and day-to-day operations. Combining practical recovery with long-term security hardening restores business continuity and builds lasting digital confidence.",
    mitigationEyebrow: "OUR RECOVERY & HARDENING APPROACH",
    mitigationTitle: "Structured 5-Stage Website Recovery Framework",
    mitigationSubtitle: "We followed a structured recovery process to bring affected websites back into a stable and usable state:",
    mitigationSteps: [
      {
        step: "01",
        name: "Website Assessment",
        desc: "Examined the affected websites to understand the extent of the compromise, identify visible issues, and assess the condition of the WordPress installation."
      },
      {
        step: "02",
        name: "Security Investigation",
        desc: "Reviewed suspicious files, website changes, plugins, themes, and available server information to identify potential sources of compromise."
      },
      {
        step: "03",
        name: "Website Recovery",
        desc: "Worked to restore the affected websites, recover available website data where necessary, and bring essential pages and functionality back online."
      },
      {
        step: "04",
        name: "WordPress Security Review",
        desc: "Reviewed the WordPress environment, including installed plugins, themes, administrator accounts, and access settings, to identify security weaknesses that needed attention."
      },
      {
        step: "05",
        name: "Hardening & Validation",
        desc: "Checked essential website functionality and applied appropriate security improvements to reduce avoidable risks and support more reliable operation."
      }
    ],
    keyFindings: [
      "Website security requires continuous attention, even after a website has been successfully restored.",
      "Regular software updates, careful plugin management, and server hardening reduce the vast majority of exploit vectors.",
      "Secure administrator accounts, strong passwords, and multi-factor access prevent unauthorized administrative breaches.",
      "Reliable off-site backups ensure rapid disaster recovery without critical content or database loss.",
      "Having a clear recovery plan makes a significant difference when an unexpected incident occurs."
    ]
  },
  {
    slug: "paineasy",
    title: "PainEasy",
    domain: "paineasy.in",
    live_url: "https://paineasy.in",
    sector: "healthcare",
    cat: "Web",
    badge: "Live Healthcare Platform",
    tagline: "Pain Management & Orthopedic Digital Care Platform",
    desc: "Integrated digital health ecosystem delivering online patient consultations, physiotherapy regimens, appointment scheduling, and automated clinical intake records.",
    overview: "PainEasy is a specialized orthopedic and digital physiotherapy healthcare platform connecting patients suffering from chronic musculoskeletal conditions with verified physical therapists, orthopedic specialists, and customized recovery regimens.",
    challenge: "Traditional orthopedic clinics suffer from 40%+ no-show rates, fragmented physical paper prescriptions, and lack of guided recovery compliance between patient appointments.",
    solution: "NeuraVolt engineered a HIPAA-compliant telemedicine web application featuring real-time video consultations, interactive exercise recovery regimens, automated SMS/WhatsApp appointment reminders, and encrypted patient EHR records.",
    results: [
      "45% reduction in appointment wait times",
      "Over 10,000+ patient sessions managed",
      "Automated intake & digital health records",
      "High patient retention & recovery tracking"
    ],
    metrics: [
      "45% reduction in appointment wait times",
      "Over 10,000+ patient sessions managed",
      "Automated intake & digital health records"
    ],
    tech: ["React", "FastAPI", "PostgreSQL", "TailwindCSS", "WebRTC"],
    logo: "/brand/paineasy-logo.svg",
    img: "/brand/paineasy-preview.png"
  },
  {
    slug: "meadylife",
    title: "MedyLife",
    domain: "medylife.com",
    live_url: "https://medylife.com",
    sector: "healthcare",
    cat: "Web",
    badge: "Live Wellness Portal",
    tagline: "Preventive Healthcare & Lifestyle Wellness Network",
    desc: "Comprehensive wellness platform bridging patients with certified nutritionists, preventive diagnostics, lifestyle biomarkers, and personalized daily health routines.",
    overview: "MedyLife focuses on preventive care and metabolic wellness, providing patients with continuous health monitoring, certified nutritionist access, and personalized daily nutrition tracking.",
    challenge: "Preventive care patients struggle with generic health advice and fragmented communication channels with clinical nutritionists.",
    solution: "Developed a responsive web platform with interactive lifestyle biomarker trackers, direct tele-nutrition consultations, and automated dietary compliance tracking.",
    results: [
      "Scalable patient health onboarding",
      "Integrated dietary & fitness schedules",
      "Multi-specialist wellness network",
      "High user engagement and dietary adherence"
    ],
    metrics: [
      "Holistic wellness & biomarker tracking",
      "Direct doctor & nutritionist consultation portal",
      "Personalized lifestyle wellness plans"
    ],
    tech: ["Next.js", "Node.js", "MongoDB", "Express", "TailwindCSS"],
    logo: "/brand/medylife-logo.png",
    img: "/brand/medylife-preview.png"
  },
  {
    slug: "launchpad",
    title: "Launchpad",
    domain: "teams.neuravolt.in/launchpad",
    live_url: "https://teams.neuravolt.in/launchpad",
    sector: "education",
    cat: "Web",
    badge: "Neuravolt Venture Platform",
    logo: "/brand/launchpad-clean.png",
    darkLogo: "/brand/launchpad-dark.png",
    tagline: "IDEAS • BUILD • SCALE | Academic & Startup Incubation Platform",
    desc: "Neuravolt's flagship venture and innovation platform built to empower university builders, student entrepreneurs, and technical teams. Provides sprint tracking, peer collaboration, mentorship boards, and developer sandboxes to scale ideas from concept to live enterprise deployments.",
    overview: "Launchpad is an academic and startup venture acceleration platform designed for university incubators, student technical clubs, and aspiring founders to transform conceptual ideas into production-ready software.",
    challenge: "Student founders face high attrition rates, disconnected team coordination, and a severe gap between academic theory and production-grade engineering.",
    solution: "NeuraVolt engineered Launchpad with structured agile milestone tracking, mentor code reviews, integrated sandbox developer environments, and live demo showcase boards.",
    results: [
      "Incubated at Amity Innovation Incubation Centre",
      "Empowering student founders & university hackathons",
      "Production-grade sandbox pipelines",
      "Accelerated MVP-to-launch cycle"
    ],
    metrics: [
      "Incubated at Amity Innovation Incubation Centre",
      "Empowering student founders & university hackathons",
      "Production-grade sandbox pipelines"
    ],
    tech: ["React", "FastAPI", "WebSockets", "PostgreSQL", "Docker"],
    img: "/brand/launchpad-preview.png"
  },
  {
    slug: "indusdeal",
    title: "IndusDeal",
    domain: "indusdeal.in",
    live_url: "https://indusdeal.in",
    sector: "b2b-industry",
    cat: "Mobile",
    badge: "Built & Maintained by NeuraVolt",
    logo: "/brand/indusdeal-icon.png",
    tagline: "B2B Industrial Sourcing & Bulk Procurement Marketplace",
    desc: "Enterprise B2B procurement marketplace connecting industrial buyers and suppliers with automated RFQ workflows, supplier catalog search, multi-party bidding, and order tracking.",
    overview: "A high-velocity digital B2B marketplace facilitating bulk industrial procurement, RFQ negotiation, supplier verification, and multi-tier logistics tracking.",
    challenge: "Opaque industrial pricing and lengthy offline procurement cycles spanning weeks for basic factory supplies.",
    solution: "Engineered a React Native mobile app and scalable backend featuring automated RFQ negotiation, supplier catalog search, multi-party bidding, and real-time order tracking.",
    results: [
      "Streamlined industrial sourcing",
      "Faster quotation turnaround",
      "Expanded supplier reach across India",
      "Automated vendor invoice reconciliation"
    ],
    metrics: [
      "60% faster quotation turnaround",
      "Multi-party RFQ bidding engine",
      "Sub-second catalog search across 50,000+ SKUs"
    ],
    tech: ["React Native", "FastAPI", "PostgreSQL", "Elasticsearch", "Docker"],
    img: "/brand/indusdeal-live-2026.png"
  },
  {
    slug: "clueblu",
    title: "ClueBlu",
    domain: "clueblu.online",
    live_url: "https://clueblu.online",
    sector: "b2b-industry",
    cat: "Web",
    badge: "Live Enterprise Platform",
    logo: "/brand/clueblu-logo.png",
    tagline: "Enterprise Strategy & Corporate Business Intelligence Network",
    desc: "Executive business intelligence and corporate strategy advisory portal providing KPI tracking, strategic decision engines, and digital enterprise growth blueprints.",
    overview: "ClueBlu provides corporate executives with interactive strategic roadmap tracking, operational KPI benchmarks, and enterprise growth diagnostics.",
    challenge: "Disjointed executive reporting across business departments causing delayed strategic decisions and unaligned quarterly roadmaps.",
    solution: "Engineered a modular corporate intelligence platform with role-based access, automated executive reporting, and strategic decision frameworks.",
    results: [
      "Real-time enterprise KPI monitoring",
      "Seamless cross-department reporting",
      "Scalable cloud-native architecture",
      "Interactive strategy decision trees"
    ],
    metrics: [
      "Executive decision modeling",
      "Multi-tenant corporate workspaces",
      "Accelerated business intelligence"
    ],
    tech: ["React", "FastAPI", "PostgreSQL", "TailwindCSS"],
    img: "/brand/clueblu-preview.png"
  },
  {
    slug: "ecopro-builders",
    title: "EcoPro Builders",
    domain: "ecoprobuilders.in",
    live_url: "https://ecoprobuilders.in",
    sector: "real-estate",
    cat: "Web",
    badge: "Designed & Built by NeuraVolt",
    logo: "/brand/ecopro-logo.png",
    tagline: "Sustainable Green Architecture & Infrastructure Firm",
    desc: "Digital enterprise portal showcasing eco-friendly construction, green building certifications, interactive architectural project galleries, and real-time client inquiry pipelines.",
    overview: "EcoPro Builders is a forward-thinking green architecture and sustainable engineering firm delivering LEED-certified commercial and residential developments.",
    challenge: "Need for high-impact visual representation of green architecture and sustainable engineering projects with sub-second page loads for 4K media.",
    solution: "Delivered a modern Next.js web application with optimized media streaming, interactive sustainability calculators, and an automated lead intake pipeline.",
    results: [
      "Enhanced green brand positioning",
      "Increased commercial project inquiries",
      "Fast core web vitals with 99+ mobile performance score",
      "Automated client quote generation"
    ],
    metrics: [
      "3x increase in commercial inquiries",
      "Sub-second load speed for 4K galleries",
      "High conversion lead generation"
    ],
    tech: ["Next.js", "Node.js", "TailwindCSS", "LeadEngine"],
    img: "/brand/ecopro-preview.png"
  },
  {
    slug: "buildhealer",
    title: "BuildHealer",
    domain: "buildhealer.com",
    live_url: "https://buildhealer.com",
    sector: "real-estate",
    cat: "Web",
    badge: "Built & Scaled by NeuraVolt",
    tagline: "Construction Repair & Verified Contractor Marketplace",
    desc: "On-demand property repair and structural maintenance platform connecting residential and commercial property owners with vetted construction professionals and transparent estimates.",
    overview: "BuildHealer bridges homeowners and commercial facilities with certified structural repair contractors, featuring transparent estimates, milestone escrows, and inspection reporting.",
    challenge: "Fragmented contractor discovery, opaque pricing, and lack of milestone tracking in residential and commercial property renovations.",
    solution: "Built a verified marketplace matching property owners with local artisans, automated quote comparisons, and digital project completion sign-offs.",
    results: [
      "Faster contractor discovery and matching",
      "Transparent quoting & escrow milestone tracking",
      "Higher project completion rates",
      "Zero contractor fraud reports"
    ],
    metrics: [
      "Faster contractor discovery",
      "100% verified artisan profiles",
      "Transparent milestone tracking"
    ],
    tech: ["React", "FastAPI", "MongoDB", "Geolocation Engine"],
    img: "/brand/buildhealer-preview.png"
  },
  {
    slug: "krishnasaree",
    title: "Krishna Saree",
    domain: "krishnasaree.in",
    live_url: "https://krishnasaree.in",
    sector: "retail",
    cat: "Web",
    badge: "Live E-Commerce Store",
    logo: "/brand/krishnasaree-logo.png",
    tagline: "Luxury Ethnic Wear & Bridal Couture E-Commerce",
    desc: "High-performance digital fashion boutique featuring curated bridal saree catalogs, custom stitching configurators, multi-currency payment processing, and automated shipping fulfillment.",
    overview: "Krishna Saree brings handcrafted heritage textiles and bridal ethnic wear to an international customer base with a frictionless mobile storefront.",
    challenge: "Traditional high-end textile e-commerce suffers from slow image loading, complex blouse custom sizing, and abandoned checkouts.",
    solution: "Built an ultrafast Next.js headless storefront with instant fabric zoom, interactive blouse tailoring inputs, Razorpay/Stripe checkout, and automated courier API dispatch.",
    results: [
      "Rapid mobile shopping experience with sub-second catalog filter",
      "Smooth payment gateway integration with 99.8% checkout success",
      "Automated order tracking and dispatch notifications via WhatsApp"
    ],
    metrics: [
      "Sub-second mobile checkout",
      "High order conversion rate",
      "Automated inventory & shipping sync"
    ],
    tech: ["Next.js", "Shopify Engine", "Razorpay", "TailwindCSS"],
    img: "/brand/krishnasaree-preview.png"
  },
  {
    slug: "pawsup",
    title: "PawsUp UAE",
    domain: "pawsup.ae",
    live_url: "https://pawsup.ae",
    sector: "retail",
    cat: "Mobile",
    badge: "Designed & Engineered by NeuraVolt",
    logo: "https://www.pawsup.ae/cdn/shop/files/Paws_Up_logo_final-01_0697a7e9-f3a1-4cba-b3bd-3d655302e402.png?v=1727108680&width=200",
    tagline: "Mobile Pet Grooming & Veterinary Services (Dubai & Abu Dhabi)",
    desc: "Leading UAE pet wellness platform enabling instant mobile grooming van dispatches, in-home veterinary visits, digital vaccination passports, and real-time GPS fleet tracking.",
    overview: "An on-demand mobile pet spa and veterinary service platform serving luxury pet owners across Dubai and Abu Dhabi.",
    challenge: "Coordinating customized mobile grooming vans with precise GPS dispatching, complex scheduling, and localized recurring bookings.",
    solution: "Engineered a Flutter mobile application with real-time van telemetry, pet biometric health records, automated calendar slots, and multi-currency billing.",
    results: [
      "Thousands of pet service bookings across Dubai and Abu Dhabi",
      "Top-rated 4.9★ UAE pet platform on iOS and Android",
      "Automated appointment scheduling with zero double-bookings"
    ],
    metrics: [
      "Thousands of pet grooming vans dispatched",
      "Top-rated 4.9★ pet service across Dubai & Abu Dhabi",
      "Automated recurring bookings"
    ],
    tech: ["Flutter", "FastAPI", "MongoDB", "Live GPS Telemetry"],
    img: "/brand/pawsup-preview.png"
  },

  {
    slug: "aqua5plus",
    title: "Aqua 5 Plus",
    domain: "aqua5plus.com",
    live_url: "https://aqua5plus.com",
    sector: "manufacturing",
    cat: "IoT",
    badge: "Live Industrial System",
    logo: "/brand/aqua5plus-logo.png",
    tagline: "Industrial RO Systems & Water Treatment Plant Telemetry",
    desc: "Digital platform and telemetry monitoring suite for commercial water purification plants, tracking TDS levels, flow rates, membrane lifecycle alerts, and nationwide distributor networks.",
    overview: "Aqua 5 Plus designs and manufactures industrial-scale reverse osmosis and water filtration units for factories, hospitals, and municipal institutions.",
    challenge: "Plant managers lacked remote visibility into TDS membrane degradation, leading to costly unplanned downtime and water quality compliance risks.",
    solution: "Engineered an IoT telemetry system streaming real-time sensor data to a centralized cloud dashboard with automated threshold alarms and maintenance dispatching.",
    results: [
      "Optimized plant uptime & filter life",
      "Automated service reminders & dealer orders",
      "Nationwide dealer transparency & warranty management"
    ],
    metrics: [
      "Real-time TDS & flow rate monitoring",
      "Centralized distributor order management",
      "Automated plant maintenance alerts"
    ],
    tech: ["React", "Node.js", "PostgreSQL", "IoT Telemetry", "MQTT"],
    img: "/brand/aqua5plus-preview.png"
  },
  {
    slug: "lockiva",
    title: "Lockiva",
    domain: "lockiva.in",
    live_url: "https://lockiva.in",
    sector: "manufacturing",
    cat: "IoT",
    badge: "Live IoT Hardware & App",
    logo: "/brand/lockiva-logo.png",
    tagline: "Smart Hardware, Biometric Security & Access Control Automation",
    desc: "Advanced smart lock ecosystem and hardware-software integration platform providing encrypted biometric access control, digital key generation, remote unlocking, and industrial security logging.",
    overview: "Lockiva develops industrial and smart home electronic access control systems combining biometric hardware, NFC, Bluetooth Low Energy, and cloud audit logging.",
    challenge: "Bridging embedded microcontroller firmware with responsive mobile apps securely without latency or security vulnerabilities.",
    solution: "Engineered an end-to-end encrypted protocol connecting Nordic/ESP32 microcontrollers with React Native mobile apps and FastAPI security microservices.",
    results: [
      "Zero unauthorized access incidents",
      "Remote key delegation via mobile app in real time",
      "Comprehensive facility security audit logs"
    ],
    metrics: [
      "Sub-50ms biometric verification",
      "Hardware-to-cloud security protocol",
      "Enterprise facility access automation"
    ],
    tech: ["Embedded C / IoT", "React Native", "FastAPI", "TLS Encryption"],
    img: "/brand/lockiva-preview.png"
  },
  {
    slug: "foxrun",
    title: "FoxRun",
    domain: "foxrun.in",
    live_url: "https://foxrun.in",
    sector: "logistics",
    cat: "Mobile",
    badge: "Designed & Maintained by NeuraVolt",
    logo: "/brand/foxrun-icon.png",
    tagline: "Real-Time Fleet Activity Tracking & Movement Telemetry",
    desc: "Cross-platform mobile application and backend engine for high-precision GPS tracking, route optimization, driver telemetry logging, and offline sensor synchronization.",
    overview: "A mobile community and movement telemetry application for running, mobility tracking, and route performance analytics.",
    challenge: "Maintaining smooth GPS tracking and high battery efficiency in varied mobile connectivity environments.",
    solution: "Engineered offline activity caching, background GPS polling, and WebSocket real-time synchronization.",
    results: [
      "Increased route accuracy and driver precision",
      "Zero data loss in poor connectivity zones",
      "Real-time movement and driver telemetry monitoring"
    ],
    metrics: [
      "Sub-second location latency",
      "Offline data persistence with auto-sync",
      "Precision route telemetry"
    ],
    tech: ["React Native", "Node.js", "MongoDB", "Geofencing", "WebSocket"],
    img: "/brand/foxrun-preview.png"
  },
  {
    slug: "ciaztravels",
    title: "Ciaz Travels",
    domain: "ciaztravels.in",
    live_url: "https://ciaztravels.in",
    sector: "logistics",
    cat: "Web",
    badge: "Live Fleet Portal",
    logo: "/brand/ciaztravels-logo.png",
    tagline: "Fleet Logistics & Corporate Passenger Transportation Network",
    desc: "Comprehensive fleet reservation and passenger transit platform managing corporate cab dispatches, route schedules, driver assignments, and automated billing.",
    overview: "A corporate mobility and vehicle fleet dispatch network managing hundreds of daily corporate commutes and intercity trips.",
    challenge: "Manual spreadsheet booking led to empty transit runs, delayed driver assignments, and billing discrepancies.",
    solution: "Delivered an automated dispatch portal with driver manifest generation, passenger SMS tracking, and automated corporate billing.",
    results: [
      "30% lower empty-vehicle transit runs",
      "Automated billing & driver manifests",
      "Real-time passenger dispatch updates"
    ],
    metrics: [
      "Automated fleet dispatching",
      "Zero-downtime booking management",
      "Corporate transit route optimization"
    ],
    tech: ["React", "FastAPI", "PostgreSQL", "SMS & WhatsApp API"],
    img: "/brand/ciaztravels-preview.png"
  },

  {
    slug: "krishi-jalsevak",
    title: "Krishi JalSevak",
    domain: "neuravolt.in",
    live_url: "https://neuravolt.in/industries/agriculture",
    sector: "agriculture",
    cat: "IoT",
    badge: "NeuraVolt Proprietary Product",
    logo: "/brand/krishi-jalsevak-page6.png",
    tagline: "Smart Hardware. Intelligent Mobile App. Designed for Indian Farmers.",
    desc: "Ruggedized 100% offline GSM smart motor controller and AI crop intelligence app engineered to eliminate dangerous 3 AM field walks for 20M+ Indian farmers.",
    overview: "Krishi JalSevak is NeuraVolt's flagship IoT hardware and AI product, engineered to solve the acute challenges faced by 20M+ Indian farmers who risk nightly field walks to operate agricultural pumps.",
    challenge: "Farmers must walk pitch-dark fields at 3 AM to check erratic rural 3-phase electricity, facing snake bites, motor burnouts from dry-runs, and heavy electricity wastage.",
    solution: "Engineered a ruggedized offline GSM motor controller that operates via simple phone calls and Hindi IVRS without needing internet, accompanied by an AI crop health mobile app.",
    results: [
      "Saves 2-4 hours of nightly field walks for rural farmers",
      "Eliminated motor burnouts via automated dry-run cutoff",
      "PM-KISAN and Startup India recognized innovation",
      "Reduced water wastage by over 30% per field cycle"
    ],
    metrics: [
      "100% offline GSM control",
      "Sub-2 second motor trip protection",
      "Over 20M+ addressable farmer market"
    ],
    tech: ["GSM Edge IoT", "Hindi IVRS", "FastAPI", "TensorFlow Lite", "React"],
    img: "/brand/krishi-jalsevak-page6.png"
  },

  {
    slug: "kolamsofindia",
    title: "Kolams of India",
    domain: "kolamsofindia.com",
    live_url: "https://kolamsofindia.com",
    sector: "arts-culture",
    cat: "Web",
    badge: "Designed & Engineered by NeuraVolt",
    logo: "https://kolamsofindia.com/wp-content/uploads/2026/07/WhatsApp-Image-2026-07-26-at-11.14.38-AM.jpeg?v=1785051234",
    tagline: "Cultural Heritage & Digital Art Preservation Archive",
    desc: "Digital archive preserving, categorizing, and celebrating traditional Kolam patterns, step-by-step tutorials, and artist contributions.",
    overview: "A cultural archive dedicated to documenting, digitizing, and animating thousands of traditional Indian Kolam mathematical geometric designs.",
    challenge: "Centuries-old folk art forms vanishing without high-fidelity digital vector documentation.",
    solution: "Engineered an interactive canvas drawing tool, cultural history database, and community pattern upload engine.",
    results: [
      "Global cultural reach with users across 40+ countries",
      "Rich digital archive with 5,000+ geometric patterns",
      "Engaged artistic and mathematical community"
    ],
    metrics: [
      "Interactive canvas drawing tool",
      "Global cultural preservation archive",
      "5,000+ vector pattern designs"
    ],
    tech: ["React", "MongoDB", "Node.js", "HTML5 Canvas"],
    img: "/brand/kolamsofindia-preview.png"
  },
  {
    slug: "vinnskolam",
    title: "vinnskolam",
    domain: "vinnskolam.com",
    live_url: "https://vinnskolam.com",
    sector: "arts-culture",
    cat: "Web",
    badge: "Digital Heritage & Art Platform",
    logo: "/brand/vinnskolam-logo.png",
    tagline: "Cultural Heritage & Digital Indian Kolam Art Repository",
    desc: "Interactive digital cultural preservation portal and mathematical art archive showcasing South Indian Kolams, sikku patterns, step-by-step design tutorials, and global artisan workshops.",
    overview: "vinnskolam is a specialized digital heritage and mathematical folk art preservation platform dedicated to documenting, digitizing, and animating thousands of traditional Indian Kolam designs for a worldwide community of artists, mathematicians, and cultural enthusiasts.",
    challenge: "Generations of sacred geometric Kolam traditions, dot matrix grid techniques, and festival rangoli motifs risked being lost without high-resolution digital vector archiving and interactive learning platforms.",
    solution: "NeuraVolt engineered a high-performance Next.js cultural repository featuring interactive dot-matrix pattern tutorials, high-resolution vector archives, responsive global workshop booking, and multi-lingual community showcase hubs.",
    results: [
      "Over 5,000+ traditional geometric patterns digitized",
      "Global cultural discovery across 45+ countries",
      "Interactive dot-grid learning tutorials",
      "High engagement international art community"
    ],
    metrics: [
      "5,000+ geometric patterns digitized",
      "Interactive dot-grid tutorials",
      "Global diaspora reach across 45+ countries"
    ],
    tech: ["Next.js", "React", "Node.js", "TailwindCSS", "Prisma"],
    img: "/brand/vinnskolam-preview.jpg"
  },
  {
    slug: "latajcollection",
    title: "latajcollection",
    domain: "latajcollection.store",
    live_url: "https://latajcollection.store",
    sector: "retail",
    cat: "E-Commerce",
    badge: "Artisanal Ethnic Fashion & Couture Store",
    tagline: "Artisanal Ethnic Fashion & Luxury Handcrafted Couture",
    desc: "Bespoke digital fashion storefront for handcrafted designer apparel, traditional ethnic wear, bridal couture, and artisanal fabric collections with seamless digital shopping.",
    overview: "latajcollection is an artisanal ethnic wear and luxury fashion storefront bringing handloom textiles, designer kurtas, festive co-ord sets, and bespoke bridal couture to modern discerning shoppers.",
    challenge: "Translating intricate handloom textile weaves, delicate embroidery, and customized garment tailoring sizing into an effortless, rapid-loading mobile shopping experience.",
    solution: "NeuraVolt designed and deployed an ultrafast responsive digital storefront featuring high-resolution fabric zoom, intuitive sizing configurators, automated inventory syncing, and frictionless multi-channel checkout.",
    results: [
      "Sub-second mobile catalog browsing & filtering",
      "Seamless payment gateway checkout with zero drop-off",
      "Automated order dispatch & customer notification pipeline",
      "Expanded reach across regional & national fashion buyers"
    ],
    metrics: [
      "Sub-second catalog browsing speed",
      "Frictionless mobile checkout flow",
      "Curated artisanal apparel catalog"
    ],
    tech: ["React", "Node.js", "TailwindCSS", "Razorpay", "PostgreSQL"],
    logo: "/brand/latajcollection-logo.png",
    darkLogo: "/brand/latajcollection-logo.png",
    img: "/brand/latajcollection-preview.jpg"
  },
  {
    slug: "dewstream",
    title: "Dewstream",
    domain: "dewstream.in",
    live_url: "https://dewstream.in",
    sector: "manufacturing",
    cat: "IoT",
    badge: "Smart RO IoT Subscription Platform",
    logo: "/brand/dewstream-logo.png",
    tagline: "Smart RO Water Purifier on Subscription | Pure Water. Zero Hassle.",
    desc: "Next-gen IoT-connected drinking water subscription ecosystem featuring automated filter diagnostics, real-time consumption telemetry, contactless billing, and zero maintenance friction.",
    overview: "Dewstream delivers smart IoT-enabled reverse osmosis (RO) drinking water purifiers on an affordable monthly subscription model with zero upfront machine cost, automated filter replacements, and real-time mobile app telemetry.",
    challenge: "Traditional home water purifiers suffer from high upfront purchase costs, predatory annual maintenance contracts, and complete lack of visibility into filter health or real-time water purity (TDS).",
    solution: "NeuraVolt built Dewstream's end-to-end digital ecosystem: combining IoT telemetry streaming TDS & water flow data to cloud backends, a modern subscriber portal for instant plan management, and automated recurring billing via Razorpay.",
    results: [
      "Zero upfront machine investment for subscribers",
      "Live real-time TDS and water consumption tracking",
      "Automated filter replacement scheduling & technician dispatch",
      "Seamless recurring subscription billing with 99.9% uptime"
    ],
    metrics: [
      "Sub-second IoT telemetry streaming",
      "Automated filter lifecycle monitoring",
      "Pan-India subscriber & technician management"
    ],
    tech: ["React", "FastAPI", "Node.js", "IoT Telemetry", "Razorpay", "TailwindCSS"],
    img: "/brand/dewstream-preview.jpg"
  }
];

export const SECTOR_LABELS = {
  "cybersecurity": "Cybersecurity",
  "healthcare": "Healthcare",
  "education": "Education",
  "b2b-industry": "B2B Industry",
  "real-estate": "Real Estate",
  "retail": "Retail",
  "manufacturing": "Manufacturing",
  "logistics": "Logistics",
  "hospitality": "Hospitality",
  "agriculture": "Agriculture",
  "government": "Government",
  "arts-culture": "Arts & Culture",
};

export const PORTFOLIO_FILTERS = [
  "All",
  "Cybersecurity",
  "Healthcare",
  "Education",
  "B2B Industry",
  "Real Estate",
  "Retail",
  "Manufacturing",
  "Logistics",
  "Hospitality",
  "Agriculture",
  "Government",
  "Arts & Culture"
];

/* ---------------- Sector Case Studies & Deployments (SEO Powered) ---------------- */
export const SECTOR_CASE_STUDIES = {
  Healthcare: {
    sector: "Healthcare",
    slug: "healthcare",
    title: "Healthcare & Digital Wellness",
    seoTitle: "Custom Healthcare Software Development & Telemedicine Solutions | NeuraVolt",
    tagline: "HIPAA-compliant telehealth platforms, clinical workflow automation, and diagnostic portals.",
    overviewDesc: "NeuraVolt engineers secure, scalable, and HIPAA-compliant healthcare software, patient management platforms, and teleconsultation systems tailored for clinics, hospitals, and medtech startups.",
    capabilities: [
      { title: "Telemedicine & Video Consultations", desc: "Encrypted WebRTC high-definition clinical video consultations with automated digital waiting rooms and prescription pads." },
      { title: "EHR & EMR Systems Integration", desc: "Seamless HL7/FHIR compliant electronic health records, lab report archiving, and multi-clinic diagnostic interoperability." },
      { title: "Clinical Workflow Automation", desc: "Intelligent patient intake, automated appointment triage, and doctor calendar scheduling with SMS/WhatsApp notifications." },
      { title: "HIPAA & Data Privacy Compliance", desc: "End-to-end AES-256 data encryption at rest and in transit, strict RBAC permissions, and comprehensive clinical audit trails." }
    ],
    faqs: [
      { q: "Does NeuraVolt develop HIPAA and GDPR compliant healthcare software?", a: "Yes. All healthcare solutions engineered by NeuraVolt adhere strictly to HIPAA, GDPR, and Indian Digital Personal Data Protection (DPDP) standards, including end-to-end encryption for medical records and strict audit logging." },
      { q: "Can your health platforms integrate with diagnostic labs and payment gateways?", a: "Absolutely. We integrate diagnostic APIs (Thyrocare, Dr. Lal PathLabs, etc.), Razorpay/Stripe medical payments, and government ABDM (Ayushman Bharat Digital Mission) protocols." },
      { q: "How do you manage patient data privacy during teleconsultations?", a: "We implement WebRTC peer-to-peer encrypted telemedicine calls, role-based access controls, and anonymized diagnostic data logging to guarantee total patient confidentiality." }
    ],
    projectSlugs: ["paineasy", "meadylife"]
  },
  Education: {
    sector: "Education",
    slug: "education",
    title: "Education & Venture Incubation",
    seoTitle: "EdTech Software Development & University Venture Incubation | NeuraVolt",
    tagline: "Venture incubation platforms, university hackathons, and regional EdTech architectures.",
    overviewDesc: "We build custom learning management systems (LMS), campus venture incubation portals, and interactive assessment platforms designed for universities, coaching institutes, and EdTech innovators.",
    capabilities: [
      { title: "Campus Incubation & Venture Platforms", desc: "Collaborative sprint tracking, peer hackathon submissions, mentorship review portals, and investor demo boards." },
      { title: "Interactive Learning Management (LMS)", desc: "Live video lectures, modular curriculum delivery, automated quiz grading, and student progress telemetry." },
      { title: "Proctored Examination Engines", desc: "AI-assisted remote proctoring, browser lockdown mechanisms, and biometric attendance authentication." },
      { title: "Institutional Analytics & ERP", desc: "Fee management, faculty scheduling, and comprehensive accreditation compliance reporting." }
    ],
    faqs: [
      { q: "What infrastructure does Launchpad provide for universities and student startups?", a: "Launchpad provides automated sprint tracking, mentor review workflows, code repository sandboxes, and demo day exhibition boards for student founders." },
      { q: "Can your EdTech platforms scale to tens of thousands of concurrent learners?", a: "Yes. Our cloud-native microservices architecture on AWS and NeuraVolt Cloud handles high-concurrency exam submissions and live streaming without degradation." },
      { q: "Can you integrate with existing university ERP systems?", a: "We build standard REST and GraphQL connectors to integrate with institutional SIS, Moodle, Canvas, and payment gateways." }
    ],
    projectSlugs: ["launchpad"]
  },
  "B2B Industry": {
    sector: "B2B Industry",
    slug: "b2b-industry",
    title: "B2B Industry & Enterprise Commerce",
    seoTitle: "B2B Marketplace & Industrial Procurement Software Development | NeuraVolt",
    tagline: "High-volume RFQ engines, industrial supply chain marketplaces, and corporate intelligence.",
    overviewDesc: "NeuraVolt develops high-performance B2B trade platforms, multi-party RFQ quotation engines, and enterprise procurement portals connecting manufacturers, distributors, and bulk buyers.",
    capabilities: [
      { title: "Automated RFQ & Multi-Party Bidding", desc: "Real-time quote requests, structured supplier counter-offers, dynamic price negotiation, and audit logs." },
      { title: "Industrial Supplier Verification", desc: "Automated GST verification, compliance document checks, and merchant rating engines." },
      { title: "Bulk Catalog Search & Elasticsearch", desc: "Sub-50ms fuzzy search indexing across 50,000+ industrial SKUs, technical datasheets, and batch volumes." },
      { title: "ERP & Accounting Synchronizers", desc: "Two-way integration with Tally, SAP, Zoho Books, and warehouse management systems for instant invoice reconciliation." }
    ],
    faqs: [
      { q: "How does your B2B RFQ engine protect pricing confidentiality between multiple bidders?", a: "We implement blind bidding protocols where vendor quotation specifics remain private and encrypted until formal bid opening stages." },
      { q: "Can your B2B marketplace handle multi-tier pricing and credit terms?", a: "Yes. We support custom merchant tier pricing, minimum order quantities (MOQs), dynamic GST calculation, and credit milestone payments." },
      { q: "Does the platform support mobile applications for warehouse floor workers?", a: "We build cross-platform React Native and Flutter mobile apps with barcode scanning, offline stock auditing, and dispatch manifests." }
    ],
    projectSlugs: ["indusdeal", "clueblu"]
  },
  "Real Estate": {
    sector: "Real Estate",
    slug: "real-estate",
    title: "Real Estate & Infrastructure Engineering",
    seoTitle: "Real Estate Software Development & PropTech Solutions | NeuraVolt",
    tagline: "Modern architectural portals, sustainable construction platforms, and verified contractor marketplaces.",
    overviewDesc: "We engineer custom PropTech solutions, interactive architectural showcase portals, construction site management platforms, and on-demand contractor marketplaces.",
    capabilities: [
      { title: "Architectural & 4K Project Portals", desc: "Ultrafast Next.js media rendering, interactive 3D floorplan tours, and LEED green certification highlights." },
      { title: "Verified Contractor Marketplaces", desc: "Two-sided property maintenance platforms with geolocation-based artisan matching and quote generation." },
      { title: "Milestone Escrow & Progress Billing", desc: "Digital milestone sign-offs, site photo verification, and transparent progress-based contractor payouts." },
      { title: "Lead Capture & CRM Automation", desc: "Instant WhatsApp and CRM synchronization capturing high-intent property buyer inquiries." }
    ],
    faqs: [
      { q: "Can your real estate platforms handle high-resolution 4K architectural renderings without slowing down?", a: "Yes. We use modern Next.js edge streaming, automated WebP/AVIF compression, and global CDN caching to ensure sub-second page loads." },
      { q: "How does the contractor verification workflow operate in platforms like BuildHealer?", a: "Contractor profiles require government ID verification, background skill certifications, and prior work audits before accepting site bookings." },
      { q: "Can you integrate real estate CRM lead capture with WhatsApp and email?", a: "We build automated webhooks connecting buyer inquiry forms directly with LeadEngine, Salesforce, HubSpot, and WhatsApp Business API." }
    ],
    projectSlugs: ["ecopro-builders", "buildhealer"]
  },
  Retail: {
    sector: "Retail",
    slug: "retail",
    title: "Retail & Consumer Commerce",
    seoTitle: "Retail & E-Commerce Software Development Company | NeuraVolt",
    tagline: "High-conversion digital storefronts, multi-currency checkout, and on-demand mobile service booking.",
    overviewDesc: "From bespoke luxury fashion storefronts to on-demand pet care booking apps in the UAE, NeuraVolt builds high-conversion retail platforms and mobile commerce engines.",
    capabilities: [
      { title: "Headless E-Commerce Storefronts", desc: "Sub-second Next.js shopping experiences with lightning-fast catalog navigation and zero layout shift." },
      { title: "On-Demand Service Booking Apps", desc: "Real-time dispatching, live GPS van tracking, and recurring calendar bookings for service fleets." },
      { title: "Omnichannel Payment Gateways", desc: "Multi-currency processing with Stripe, Razorpay, Apple Pay, Tabby, and Tamara Buy-Now-Pay-Later." },
      { title: "Automated Inventory & Courier Logistics", desc: "Real-time stock sync with Shiprocket, DHL, and local courier APIs with automated customer tracking alerts." }
    ],
    faqs: [
      { q: "How do your retail applications handle high traffic spikes during seasonal flash sales?", a: "Our headless architectures decouple the frontend edge from backend microservices, allowing horizontal autoscaling that absorbs thousands of concurrent checkouts." },
      { q: "Can you build on-demand mobile service booking platforms like PawsUp UAE?", a: "Yes. We build native-performance Flutter apps equipped with real-time GPS fleet telemetry, multi-slot booking calendars, and recurring subscriptions." },
      { q: "Do you integrate custom product configurators for apparel or furniture?", a: "We build interactive 3D/canvas customizers (like blouse stitching measurements or furniture dimension estimators) directly into the product page." }
    ],
    projectSlugs: ["krishnasaree", "pawsup", "latajcollection"]
  },
  Manufacturing: {
    sector: "Manufacturing",
    slug: "manufacturing",
    title: "Manufacturing & Smart Hardware",
    seoTitle: "Smart Manufacturing & Industrial IoT Software Solutions | NeuraVolt",
    tagline: "Industrial IoT telemetry, water filtration monitoring, and biometric hardware access control.",
    overviewDesc: "NeuraVolt bridges embedded hardware and cloud software, engineering real-time industrial telemetry platforms, predictive maintenance alerts, and biometric security systems.",
    capabilities: [
      { title: "Industrial IoT Telemetry & MQTT", desc: "Live sensor telemetry streaming for TDS levels, flow rates, temperature, and electrical power consumption." },
      { title: "Predictive Maintenance & Alarms", desc: "Automated degradation warnings preventing costly equipment breakdowns before plant downtime occurs." },
      { title: "Biometric Access Control & Firmware", desc: "Sub-50ms hardware verification, encrypted digital key generation, and enterprise security audit logging." },
      { title: "Nationwide Distributor Portals", desc: "Centralized dealer networks, warranty verification databases, and spare parts ordering engines." }
    ],
    faqs: [
      { q: "Can your IoT platforms connect with legacy factory machinery and PLCs?", a: "Yes. We support standard industrial protocols including Modbus, MQTT, RS485, and OPC-UA, converting legacy telemetry into cloud dashboards." },
      { q: "How secure is the communication between smart locks/hardware and cloud servers?", a: "All hardware-to-cloud telemetry is secured using TLS 1.3 encryption, cryptographic device certificates, and rotating cryptographic keys." },
      { q: "What latency should we expect for real-time industrial monitoring dashboards?", a: "Our WebSocket and MQTT broker pipelines deliver telemetry updates to browser dashboards in less than 150 milliseconds." }
    ],
    projectSlugs: ["aqua5plus", "lockiva", "dewstream"]
  },
  Logistics: {
    sector: "Logistics",
    slug: "logistics",
    title: "Logistics & Fleet Operations",
    seoTitle: "Logistics & Fleet Management Software Development | NeuraVolt",
    tagline: "Real-time GPS telemetry, route optimization, and corporate transportation dispatches.",
    overviewDesc: "We build mission-critical logistics software, driver dispatch systems, passenger transit platforms, and real-time movement telemetry engines.",
    capabilities: [
      { title: "High-Precision GPS & Telemetry", desc: "Sub-second location logging, speed monitoring, geofence alerts, and low-latency driver tracking." },
      { title: "Offline Persistence & Auto-Sync", desc: "Mobile apps that continue logging sensor and trip data in zero-connectivity rural zones, auto-syncing when online." },
      { title: "Automated Route Optimization", desc: "Intelligent dispatch algorithms minimizing empty return runs and reducing fuel overhead." },
      { title: "Corporate Manifests & Automated Billing", desc: "Instant driver assignment manifests, passenger SMS notifications, and corporate GST billing." }
    ],
    faqs: [
      { q: "How does the mobile app handle GPS tracking in remote areas with no cellular network?", a: "We engineer local SQLite/Realm offline caching in React Native and Flutter. All trip coordinates and timestamps persist locally and sync to the cloud upon signal restoration." },
      { q: "Can your fleet management system integrate with corporate ERPs for billing?", a: "Yes. We build automated billing microservices that generate consolidated monthly GST invoices and employee trip expense audits." },
      { q: "Does the platform support driver behavior analysis?", a: "We track acceleration, hard braking, idle time, and average route efficiency to provide fleet safety scorecards." }
    ],
    projectSlugs: ["foxrun", "ciaztravels"]
  },
  Hospitality: {
    sector: "Hospitality",
    slug: "hospitality",
    title: "Hospitality & Local Discovery",
    seoTitle: "Hospitality & Hyperlocal Discovery Platform Development | NeuraVolt",
    tagline: "Hyperlocal dining discovery, venue bookings, and city directory engines.",
    overviewDesc: "NeuraVolt engineers scalable city discovery platforms, restaurant reservation engines, interactive event directories, and merchant self-service portals.",
    capabilities: [
      { title: "Hyperlocal Geolocation Search", desc: "Instant distance-based venue discovery powered by Algolia and PostGIS spatial indexing." },
      { title: "Merchant Self-Service Portals", desc: "Business owner dashboards to update menus, post promotional discounts, and respond to verified customer reviews." },
      { title: "Interactive Event & Dining Calendars", desc: "Real-time table bookings, event RSVP tracking, and ticketing integrations." },
      { title: "Community Reviews & Ratings Engine", desc: "Fraud-resistant review moderation pipelines ensuring trusted recommendations for travelers and locals." }
    ],
    faqs: [
      { q: "Can your hyperlocal platform scale to thousands of simultaneous dining listings?", a: "Yes. Our geospatial indexing architecture handles rapid sub-second location radius queries across tens of thousands of venue coordinates." },
      { q: "Can local business owners update their own listings and menus?", a: "Yes. We deliver dedicated merchant dashboards allowing business managers to update pricing, operating hours, photos, and promotions in real time." },
      { q: "Do you provide white-label city directory solutions?", a: "We customize discovery platforms for tourism boards, city councils, and private hospitality networks." }
    ],
    projectSlugs: []
  },
  Agriculture: {
    sector: "Agriculture",
    slug: "agriculture",
    title: "Agriculture & Rural Deep Tech",
    seoTitle: "AgriTech Software & IoT Smart Motor Controllers | Krishi JalSevak India",
    tagline: "Smart irrigation hardware, 100% offline GSM control, and AI farm intelligence.",
    overviewDesc: "Featuring Krishi JalSevak, NeuraVolt's flagship AgriTech hardware and AI ecosystem engineered to eliminate dangerous 3 AM field walks for 20M+ Indian farmers.",
    capabilities: [
      { title: "100% Offline GSM Remote Control", desc: "Control 3-phase and 1-phase agricultural pumps via a simple missed call or phone call with zero internet required." },
      { title: "Hindi & Vernacular IVRS Voice Alerts", desc: "Clear voice feedback speaking directly to farmers in regional languages regarding power status and motor health." },
      { title: "Comprehensive Motor Protection Suite", desc: "Instant trip protection against phase failure, high/low voltage fluctuations, and dry-run pump burnout." },
      { title: "AI Crop Intelligence Mobile Application", desc: "Leaf scan disease diagnosis, vernacular agronomy copilot, and hyperlocal weather intelligence." }
    ],
    faqs: [
      { q: "Does Krishi JalSevak require WiFi or 4G mobile internet to operate?", a: "No. The Krishi JalSevak smart motor controller works 100% offline using standard 2G/GSM cellular signals. Farmers can start or stop their pump using a simple missed call or keypad phone." },
      { q: "How does the dry-run protection prevent motor burnout?", a: "Our onboard sensors monitor current draw in real time. If water in the borehole runs out, the unit trips the starter within 3 seconds, saving expensive submersible rewinding costs." },
      { q: "What is the difference between Home Edition and Pro Edition?", a: "Home Edition (₹5,999) includes GSM call control, Hindi voice alerts, and the full motor protection suite. Pro Edition (₹7,999) adds the AI Farm Intelligence app, GPS anti-theft tracking, and advanced soil/irrigation analytics." }
    ],
    isProduct: true,
    product: {
      name: "Krishi JalSevak",
      subtitle: "Flagship IoT & AI Product of NeuraVolt Technology",
      tagline: "Smart Hardware. Intelligent Mobile App. Designed for Indian Farmers.",
      desc: "Solving the sleepless 3 AM field walk for 20M+ Indian farmers. A ruggedized smart motor controller operating 100% offline via GSM phone calls and Hindi IVRS, paired with an AI crop intelligence mobile application.",
      hardwareSpecs: [
        { title: "GSM Call Control", desc: "Start and stop pump remotely via simple phone call with zero internet required." },
        { title: "Hindi IVRS", desc: "Clear regional voice alerts speaking directly to the farmer in Hindi." },
        { title: "Safe Voltage Protection", desc: "Safe voltage range protects expensive submersible motors from burnout." },
        { title: "Dry Run Detection", desc: "Detects water runout instantly and stops pump before motor damage occurs." },
        { title: "GPS Anti-Theft", desc: "Live device location tracking keeps equipment secure in remote fields." },
        { title: "Local Data Storage", desc: "Complete operational logging stored locally on onboard flash memory." }
      ],
      aiSpecs: [
        { title: "Crop Health Detection", desc: "Early visual anomaly and pest detection using AI." },
        { title: "Disease Detection", desc: "Instant leaf scan diagnosis with remedial guidance." },
        { title: "AI Assistant", desc: "Vernacular voice assistant for agronomy and soil queries." },
        { title: "Weather Intelligence", desc: "Hyperlocal precipitation forecasts and wind alerts." },
        { title: "Farm Analytics", desc: "Complete pump run-time history, flow rates, and water usage." }
      ],
      editions: [
        {
          name: "Home Edition",
          price: "₹5,999",
          type: "One-time",
          features: ["GSM Call Control", "Hindi IVRS Voice", "Motor Protection Suite", "Smart Timer", "100% Offline Reliable"]
        },
        {
          name: "Pro Edition",
          price: "₹7,999",
          type: "One-time",
          highlight: true,
          features: ["Everything in Home Edition", "AI Farm Intelligence App", "GPS Anti-Theft & Alerts", "Advanced Soil & Yield Analytics", "Multi-Farm Management"]
        }
      ],
      slides: [
        { page: 5, src: "/brand/krishi-jalsevak-page5.png", title: "The Problem We Saw" },
        { page: 6, src: "/brand/krishi-jalsevak-page6.png", title: "Built Offline. Powered by AI." },
        { page: 7, src: "/brand/krishi-jalsevak-page7.png", title: "Product Editions & 3-Phase Roadmap" },
        { page: 8, src: "/brand/krishi-jalsevak-page8.png", title: "Market Opportunity & Why We Win" }
      ],
      marketImpact: [
        "20M+ Target Farmers across MP, UP, and Rajasthan",
        "Saves 2-4 hours of dangerous nightly field walks",
        "Saves water, saves power, saves money",
        "Backed by PM-KISAN, Startup India, Digital India & Amity Innovation Incubation Centre"
      ]
    },
    projectSlugs: ["krishi-jalsevak"]
  },
  Government: {
    sector: "Government",
    slug: "government",
    title: "Government & Civic Digital Infrastructure",
    seoTitle: "GovTech & Civic Digital Infrastructure Development | NeuraVolt",
    tagline: "Public digital infrastructure, rural smart telemetry, and academic incubation initiatives.",
    overviewDesc: "NeuraVolt collaborates with public institutions, university incubators, and civic initiatives to build resilient, accessible, and transparent digital public infrastructure.",
    capabilities: [
      { title: "Rural Civic Telemetry & Monitoring", desc: "Solar-compatible IoT edge units monitoring village utility pumps, water tanks, and energy grids." },
      { title: "Government Scheme Alignment (PM-KISAN / UBA)", desc: "Software workflows aligned with national agricultural and rural development missions." },
      { title: "Secure Citizen Portals & Open APIs", desc: "Compliant, accessible citizen service interfaces supporting multi-regional languages and Aadhaar/OTP auth." },
      { title: "Academic Incubation & Research Pipelines", desc: "Joint R&D pipelines backed by Amity Innovation Incubation Centre (AIIC) and Startup India." }
    ],
    faqs: [
      { q: "Is NeuraVolt a DPIIT-recognized startup?", a: "Yes, NeuraVolt (Voltnovate Solutions LLP) is officially recognized by the Department for Promotion of Industry and Internal Trade (DPIIT) under Startup India." },
      { q: "Can your GovTech platforms operate in low-bandwidth rural environments?", a: "Yes. Our edge architectures are built with an offline-first philosophy, utilizing SMS, low-bitrate telemetry, and client-side data persistence." },
      { q: "How do you handle public sector data sovereignty and compliance?", a: "All data is hosted strictly within Indian sovereign cloud regions (MeitY empaneled data centers) with strict cryptographic auditing." }
    ],
    projectSlugs: []
  },
  "Arts & Culture": {
    sector: "Arts & Culture",
    slug: "arts-culture",
    title: "Arts, Culture & Digital Heritage Preservation",
    seoTitle: "Cultural Heritage & Digital Archive Platform Development | NeuraVolt",
    tagline: "Interactive geometric archives, cultural preservation platforms, and global artisan repositories.",
    overviewDesc: "NeuraVolt crafts high-performance digital archives, mathematical canvas design engines, and cultural preservation platforms connecting traditional folk artisans with global audiences.",
    capabilities: [
      { title: "Mathematical Vector & Canvas Rendering", desc: "Interactive HTML5 canvas engines allowing users to draw, simulate, and export traditional geometric folk patterns in lossless vector formats." },
      { title: "Digital Heritage Preservation & Archiving", desc: "Cloud indexing of thousands of traditional heritage designs, cultural folklore stories, and artisan histories with sub-second retrieval." },
      { title: "Global Cultural E-Commerce & Workshops", desc: "Integrated workshop ticketing, artisan masterclasses, and international multi-currency souvenir checkout." },
      { title: "Community Submissions & Moderation", desc: "Crowdsourced folklore pattern uploads with automated copyright checking and metadata categorization." }
    ],
    faqs: [
      { q: "How does the digital archive preserve intricate geometric art like Kolams?", a: "We engineer custom HTML5 canvas and SVG vector generators that capture the exact mathematical coordinate matrices of traditional dot and line patterns." },
      { q: "Can the platform handle international workshop ticketing and e-commerce?", a: "Yes. The platform integrates global payment processors (Stripe, Razorpay, PayPal) with automated currency conversion and automated webinar access passes." },
      { q: "Is the archive accessible on low-end mobile devices?", a: "Yes. Our lightweight frontend ensures smooth 60fps canvas rendering and responsive image galleries even on budget smartphones." }
    ],
    projectSlugs: ["kolamsofindia", "vinnskolam"]
  }
};

// Aliases
SECTOR_CASE_STUDIES["Finance"] = SECTOR_CASE_STUDIES["B2B Industry"];

// Pre-populate .projects on each sector for direct property access
Object.values(SECTOR_CASE_STUDIES).forEach((sec) => {
  if (sec.projectSlugs) {
    sec.projects = sec.projectSlugs.map((slug) => PROJECTS.find((p) => p.slug === slug)).filter(Boolean);
  }
});

export const getSectorBySlug = (slug) => {
  const norm = (slug || "").toLowerCase().trim();
  if (norm === "finance") return SECTOR_CASE_STUDIES["B2B Industry"];
  const sector = Object.values(SECTOR_CASE_STUDIES).find(
    (s) => s.slug === norm || s.sector.toLowerCase().replace(/[\s_]+/g, "-") === norm
  );
  if (!sector) return null;
  // Always ensure fresh resolution from PROJECTS
  const resolvedProjects = sector.projectSlugs
    ? sector.projectSlugs.map((s) => PROJECTS.find((p) => p.slug === s)).filter(Boolean)
    : PROJECTS.filter((p) => p.sector === sector.slug);

  return {
    ...sector,
    projects: resolvedProjects
  };
};

/* ---------------- Process ---------------- */
export const PROCESS = [
  { n: "01", title: "Understanding Your Business", icon: Search },
  { n: "02", title: "Planning", icon: Layers },
  { n: "03", title: "Design", icon: PenTool },
  { n: "04", title: "Development", icon: Code2 },
  { n: "05", title: "Testing", icon: TestTube2 },
  { n: "06", title: "Deployment", icon: Rocket },
  { n: "07", title: "Support", icon: LifeBuoy },
  { n: "08", title: "Maintenance", icon: Wrench },
];


/* ---------------- Technologies ---------------- */
export const TECHNOLOGIES = [
  { name: "React", logo: "https://cdn.simpleicons.org/react/61DAFB" },
  { name: "Next.js", logo: "https://cdn.simpleicons.org/nextdotjs/ffffff" },
  { name: "Python", logo: "https://cdn.simpleicons.org/python/3776AB" },
  { name: "FastAPI", logo: "https://cdn.simpleicons.org/fastapi/009688" },
  { name: "Flutter", logo: "https://cdn.simpleicons.org/flutter/02569B" },
  { name: "React Native", logo: "https://cdn.simpleicons.org/react/61DAFB" },
  { name: "Node.js", logo: "https://cdn.simpleicons.org/nodedotjs/5FA04E" },
  { name: "TypeScript", logo: "https://cdn.simpleicons.org/typescript/3178C6" },
  { name: "LLMs & GenAI", logo: "https://cdn.simpleicons.org/huggingface/FFD21E" },
  { name: "Docker", logo: "https://cdn.simpleicons.org/docker/2496ED" },
  { name: "Kubernetes", logo: "https://cdn.simpleicons.org/kubernetes/326CE5" },
  { name: "AWS", logo: "/brand/tech-aws.svg" },
  { name: "GCP", logo: "https://cdn.simpleicons.org/googlecloud/4285F4" },
  { name: "PostgreSQL", logo: "https://cdn.simpleicons.org/postgresql/4169E1" },
  { name: "MongoDB", logo: "https://cdn.simpleicons.org/mongodb/47A248" },
  { name: "Redis", logo: "https://cdn.simpleicons.org/redis/FF4438" },
  { name: "Firebase", logo: "https://cdn.simpleicons.org/firebase/DD2C00" },
  { name: "TensorFlow", logo: "https://cdn.simpleicons.org/tensorflow/FF6F00" },
  { name: "Java", logo: "/brand/tech-java.svg" },
  { name: "Laravel", logo: "https://cdn.simpleicons.org/laravel/FF2D20" },
  { name: "Tailwind CSS", logo: "https://cdn.simpleicons.org/tailwindcss/06B6D4" }
];

/* ---------------- Why choose ---------------- */
export const WHY = [
  { title: "AI First Approach", desc: "Prioritising AI to make your business ready for tomorrow." },
  { title: "Fast Delivery", desc: "Delivering 99.99% platform uptime." },
  { title: "Scalable Architecture", desc: "Built for users from all demographics." },
  { title: "Transparent Communication", desc: "Delivered through real dashboards." },
  { title: "Modern Tech Stack", desc: "Future-proof tools chosen for longevity." },
  { title: "Dedicated Support", desc: "A team that stays after launch." },
  { title: "Global Standards", desc: "Security, accessibility & compliance." },
  { title: "Design Excellence", desc: "Designed for excellent user experience." },
];

/* ---------------- Testimonials (Client Reviews) ---------------- */
export const TESTIMONIALS = [
  {
    quote: "Neuravolt transformed our vision into an exceptional web platform. Their team demonstrated outstanding technical expertise, prompt communication, and delivered a seamless digital experience for our community. Truly professional and reliable partners.",
    name: "Hitesh Kumar Sahu",
    role: "Founder, FoxRun",
    rating: 5,
    company: "FoxRun",
  },
  {
    quote: "From quotation to completion, everything was genuine and professional. They delivered exactly what was promised, with great efficiency and commitment. Truly reliable and trustworthy website developers. Highly recommended!",
    name: "Vins Raj",
    role: "Founder, VinnsKolam",
    rating: 5,
    company: "VinnsKolam",
  },
  {
    quote: "Building a healthcare platform requires precision, user trust, and speed. Neuravolt delivered an intuitive patient experience and a robust backend system that streamlined our bookings effortlessly. Outstanding work!",
    name: "Prateek",
    role: "Founder & Director, PainEasy",
    rating: 5,
    company: "PainEasy",
  },
  {
    quote: "Neuravolt built our B2B industrial marketplace with automated RFQ workflows and vendor management. Their technical depth, responsive development cycle, and commitment to quality exceeded our expectations.",
    name: "Imran Zaheer",
    role: "Managing Director, IndusDeal",
    rating: 5,
    company: "IndusDeal",
  },
  {
    quote: "The security and performance standards Neuravolt implemented for our platform were top-notch. They handled complex architecture with ease, delivering a modern, high-speed solution ahead of schedule.",
    name: "Gurmeet Singh",
    role: "Director, ClueBlu",
    rating: 5,
    company: "ClueBlu",
  },
  {
    quote: "Neuravolt gave our construction firm a premier digital presence. Their attention to detail, sleek design aesthetic, and proactive approach made the entire development process seamless. Highly satisfied with their service.",
    name: "Hr Pradeep Kumar",
    role: "Managing Partner, EcoPro Builders",
    rating: 5,
    company: "EcoPro Builders",
  },
  {
    quote: "Connecting homeowners with verified contractors for construction repair required absolute reliability. Neuravolt engineered and shipped our web platform in record time with unmatched stability.",
    name: "Hr Pradeepkumar",
    role: "CEO, BuildHealer",
    rating: 5,
    company: "BuildHealer",
  },
  {
    quote: "These people are very talented. And their behavior towards customers is also very good. I am taking their service from a long time and I have not faced any problem till now.",
    name: "Neeraj Rankawat",
    role: "Founder, Krishna Saree",
    rating: 5,
    company: "Krishna Saree",
  },
  {
    quote: "Our pet care platform needed a friendly, modern design coupled with a swift booking workflow. Neuravolt executed every requirement to perfection. The user feedback has been overwhelmingly positive!",
    name: "Sneha",
    role: "Founder & Operations Lead, PawsUp",
    rating: 5,
    company: "PawsUp",
  },
];


export const TEAM = [
  { name: "Ajay Pratap Tomar", role: "Co-Founder & CTO", initials: "AT", img: "/brand/team/ajay.png", fallbackImg: "/uploads/img_8fa772417d0c.png", linkedin: "https://www.linkedin.com/company/neuravolt-technology/posts/?feedView=all", bio: "Architecting AI first enterprise systems, deeptech solutions, and scalable cloud infrastructure." },
  { name: "Hardik Chandra", role: "Co-Founder & CEO", initials: "HC", img: "/brand/team/hardik.png", fallbackImg: "/uploads/img_013c91a32ec6.png", linkedin: "https://www.linkedin.com/company/neuravolt-technology/posts/?feedView=all", bio: "Driving strategic business vision, global client partnerships, and company growth." },
  { name: "Kratika Rajawat", role: "Operations Associate", initials: "KR", img: "/brand/team/kratika.png", fallbackImg: "/uploads/img_696d6721f58c.png", linkedin: "https://www.linkedin.com/company/neuravolt-technology/posts/?feedView=all", bio: "Orchestrating product engineering workflows, agile delivery, and operational excellence." },
  { name: "Sonali Senger", role: "Business Operations Manager", initials: "SS", img: "/brand/team/sonali.jpeg", fallbackImg: "/uploads/img_e17485eb38b3.jpeg", linkedin: "https://www.linkedin.com/company/neuravolt-technology/posts/?feedView=all", bio: "Managing client engagement, business operations, and high-velocity product execution." }
];

/* ---------------- Blogs (starter, editable via CMS) ---------------- */
export const BLOGS = BLOGS_DATA;


/* ---------------- Careers (editable via CMS) ---------------- */
export const JOBS = [
  { title: "Frontend Developer", type: "Full-time", location: "Hybrid (HQ / Flexible)", team: "Engineering" },
  { title: "Backend Developer", type: "Full-time", location: "Hybrid (HQ / Flexible)", team: "Engineering" },
  { title: "Flutter Developer", type: "Full-time", location: "Hybrid (HQ / Flexible)", team: "Mobile" },
  { title: "UI/UX Designer", type: "Full-time", location: "Hybrid (HQ / Flexible)", team: "Design" },
  { title: "AI/ML Engineer", type: "Full-time", location: "Hybrid (HQ / Flexible)", team: "AI" },
  { title: "QA Engineer", type: "Full-time", location: "Hybrid (HQ / Flexible)", team: "Quality" },
  { title: "DevOps Engineer", type: "Full-time", location: "Hybrid (HQ / Flexible)", team: "Infrastructure" },
  { title: "Business Development Executive", type: "Full-time", location: "On-site (Gurugram/Gwalior)", team: "Growth" },
];
export const INTERNSHIPS = [
  { title: "Frontend Intern", type: "Internship", location: "Hybrid (HQ / Flexible)", team: "Engineering" },
  { title: "Backend Intern", type: "Internship", location: "Hybrid (HQ / Flexible)", team: "Engineering" },
  { title: "UI/UX Design Intern", type: "Internship", location: "Hybrid (HQ / Flexible)", team: "Design" },
  { title: "AI/ML Intern", type: "Internship", location: "Hybrid (HQ / Flexible)", team: "AI" },
];

/* ---------------- Clients (editable via CMS) ---------------- */
export const CLIENTS = [
  { name: "PainEasy", domain: "paineasy.in", logo: "/brand/paineasy-logo.svg" },
  { name: "MedyLife", domain: "medylife.com", logo: "/brand/medylife-logo.png" },
  { name: "Launchpad", domain: "teams.neuravolt.in/launchpad", logo: "/brand/launchpad-clean.png" },
  { name: "IndusDeal", domain: "indusdeal.in", logo: "/brand/indusdeal-icon.png" },
  { name: "ClueBlu", domain: "clueblu.online", logo: "/brand/clueblu-logo.png" },
  { name: "EcoPro Builders", domain: "ecoprobuilders.in", logo: "/brand/ecopro-logo.png" },
  { name: "BuildHealer", domain: "buildhealer.com", logo: "/brand/buildhealer-icon.png" },
  { name: "Krishna Saree", domain: "krishnasaree.in", logo: "/brand/krishnasaree-logo.png" },
  { name: "PawsUp UAE", domain: "pawsup.ae", logo: "/brand/pawsup-icon.png" },
  { name: "Lockiva", domain: "lockiva.com", logo: "/brand/lockiva-logo.png" },
  { name: "FoxRun", domain: "foxrun.in", logo: "/brand/foxrun-logo.png" },
  { name: "Ciaz Travels", domain: "ciaztravels.in", logo: "/brand/ciaztravels-logo.png" },
  { name: "vinnskolam", domain: "vinnskolam.com", logo: "/brand/vinnskolam-logo.png" },
];



/* ---------------- FAQ ---------------- */
export const FAQS = [
  { q: "How quickly can you start a project?", a: "Most engagements kick off within 5 to 7 business days after a discovery call and scope alignment." },
  { q: "Do you work with startups and enterprises?", a: "Yes, from pre-seed MVPs to enterprise digital transformation, we scale our team to your stage." },
  { q: "What does the AI first approach mean?", a: "We evaluate every workflow for intelligent automation, embedding ML and LLM capability where it creates real leverage." },
  { q: "How do you handle data security?", a: "We follow global standards: encryption in transit and at rest, least-privilege access, and compliance-ready architecture." },
  { q: "What is your pricing model?", a: "Fixed-scope, retainer, or dedicated-team models. We recommend the best fit during discovery." },
  { q: "Do you provide post-launch support?", a: "Absolutely, dedicated support and continuous optimization are core to how we work." },
];

/* ---------------- Service-Specific FAQs ---------------- */
export const SERVICE_FAQS = {
  "ai-solutions": [
    {
      q: "How do you choose between fine-tuning open-source LLMs vs using commercial APIs?",
      a: "We evaluate latency, operational cost, accuracy, and data sovereignty. For private enterprise IP and zero data retention risk, we fine-tune open-weights models (such as Llama 3 or Mistral) on dedicated private VPCs. For general cognitive tasks, we architect hybrid pipelines using commercial APIs with vectorized prompt caching and data sanitization."
    },
    {
      q: "How do you eliminate hallucinations in enterprise AI systems?",
      a: "We deploy advanced Retrieval-Augmented Generation (RAG) with hybrid dense-sparse vector search, cross-encoder re-ranking, and strict citation thresholds. Guardrails like NeMo and deterministic assertion layers enforce factual accuracy before any response is rendered."
    },
    {
      q: "Can your AI solutions integrate with our existing databases and legacy software?",
      a: "Yes. We engineer secure tool-calling interfaces and API connectors over SQL/NoSQL databases, REST/GraphQL endpoints, and enterprise ERPs. AI agents can safely query data and execute validated business workflows under role-based access controls."
    },
    {
      q: "What is your typical timeline for delivering a functional AI prototype or MVP?",
      a: "A functional proof-of-concept or pilot typically ships in 2 to 4 weeks. Full enterprise multi-agent workflows with automated evaluation benchmarks, latency optimization, and security audits generally deploy within 6 to 10 weeks."
    },
    {
      q: "How do you ensure data privacy, GDPR, and DPDP compliance for AI models?",
      a: "We ensure zero customer data is used for public model training. All inference runs in your isolated VPC or dedicated bare-metal infrastructure with AES-256 encryption at rest and in transit, complete PII redaction, and auditable logging."
    },
    {
      q: "Do you provide ongoing model monitoring, evaluation, and MLOps support?",
      a: "Yes. We implement real-time drift detection, response quality scoring, automated re-indexing pipelines, and human-in-the-loop review queues to keep your models accurate and performant over time."
    }
  ],
  "software-development": [
    {
      q: "What technologies and architectural patterns do you use for custom software?",
      a: "We architect scalable systems tailored to your concurrency requirements, using modern frontends (React, Next.js, TypeScript), high-throughput backends (Node.js, Go, Python), and resilient data tiers (PostgreSQL, Redis, Kafka/RabbitMQ) with modular microservice or clean monolith patterns."
    },
    {
      q: "Who owns the source code and intellectual property (IP)?",
      a: "You retain 100% ownership of all source code, architecture documentation, design assets, and database schemas from day one. We hand over full repository access with zero proprietary licensing lock-in."
    },
    {
      q: "How do you guarantee high performance and system scalability under heavy load?",
      a: "We design cloud-native systems with horizontal auto-scaling, distributed caching layers, connection pooling, optimized database indexing, and asynchronous queue processing, thoroughly verified through automated stress and load testing."
    },
    {
      q: "How do you handle scope changes and sprint priorities during development?",
      a: "We operate in two-week Agile sprints with transparent milestone reviews, shared Jira/Linear boards, and direct engineer communication via Slack/Teams, allowing you to reprioritize backlog items seamlessly as your business needs evolve."
    },
    {
      q: "Can the new software integrate with our existing third-party platforms and legacy tools?",
      a: "Yes. We build custom bi-directional REST, GraphQL, and webhook adapters to synchronize data with payment gateways, ERPs, CRMs, accounting systems, and proprietary legacy databases without disruption."
    },
    {
      q: "What warranty and post-launch maintenance do you provide?",
      a: "Every custom software delivery includes a 30-day post-launch warranty for bug fixes and stability monitoring, followed by dedicated SLA support plans covering continuous enhancements, security patches, and infrastructure monitoring."
    }
  ],
  "web-development": [
    {
      q: "How do you guarantee sub-second load times and high Core Web Vitals scores?",
      a: "We leverage modern SSR/SSG architectures (Next.js/React), automated asset optimization (AVIF/WebP image compression, dynamic code splitting, tree shaking), and global edge CDN caching to achieve near-instant First Contentful Paint and eliminate layout shift."
    },
    {
      q: "Will our website or web application be fully responsive across all screens?",
      a: "Yes. Every interface is meticulously tested across mobile, tablet, laptop, and ultra-wide desktop viewports, ensuring adaptive typography, touch-optimized interactions, and flawless rendering on all major browsers."
    },
    {
      q: "Do you build web platforms with technical SEO and search indexability built in?",
      a: "Yes. We configure server-side rendering for complete search crawler indexing, automated XML sitemaps, structured JSON-LD schema markup, dynamic OpenGraph meta tags, and canonical URL routing out of the box."
    },
    {
      q: "Can non-technical team members easily update content and manage pages?",
      a: "Yes. We integrate user-friendly headless CMS solutions (such as Sanity, Strapi, or Payload) with custom component blocks so your marketing and editorial teams can create pages and edit content without developer assistance."
    },
    {
      q: "How do you secure web applications against modern web vulnerabilities?",
      a: "We implement strict Content Security Policies (CSP), HTTP security headers (HSTS, X-Frame-Options), CSRF/XSS protection middleware, rate limiting, and automated SSL/TLS certificate management."
    },
    {
      q: "Can you build complex web applications such as SaaS dashboards and client portals?",
      a: "Yes. We engineer advanced single-page applications and multi-tenant SaaS portals with role-based authentication, real-time WebSockets, interactive charting, billing integrations, and complex data tables."
    }
  ],
  "mobile-apps": [
    {
      q: "Do you recommend Flutter, React Native, or native iOS/Android development?",
      a: "For over 90% of applications, modern cross-platform frameworks (Flutter or React Native) deliver 60fps native performance, 40% faster time-to-market, and a unified codebase for both iOS and Android. For deep hardware-level integrations, we build native Swift or Kotlin modules."
    },
    {
      q: "Do you manage the entire Apple App Store and Google Play Store submission process?",
      a: "Yes. We handle end-to-end publishing, including developer account setup, provisioning certificates, store asset preparation, privacy questionnaires, compliance checks, and resolving any app review feedback until approval."
    },
    {
      q: "How do you handle offline functionality and data synchronization in mobile apps?",
      a: "We architect offline-first mobile apps using encrypted local databases (SQLite/Hive/WatermelonDB) with automated background synchronization and conflict resolution when network connectivity is restored."
    },
    {
      q: "Can you integrate mobile push notifications, biometrics, and device hardware?",
      a: "Yes. We implement targeted push notifications (Firebase Cloud Messaging, OneSignal), biometric authentication (Face ID, Fingerprint), GPS geo-tracking, Bluetooth/BLE peripheral communication, and camera scanning."
    },
    {
      q: "How do you ensure smooth animations and minimal battery consumption?",
      a: "We profile applications using Xcode Instruments and Android Profiler, optimizing memory allocations, reducing background thread overhead, and batching network requests to maximize battery efficiency and frame rates."
    },
    {
      q: "How do you handle app updates after release without waiting for store reviews?",
      a: "We implement Over-The-Air (OTA) update pipelines (such as Expo EAS or CodePush) for instant JavaScript and asset updates, alongside automated Fastlane CI/CD pipelines for scheduled native store releases."
    }
  ],
  "cloud-infrastructure": [
    {
      q: "What makes neuravolt.cloud different from hyperscalers like AWS or Google Cloud?",
      a: "Neuravolt Cloud offers dedicated bare-metal performance, predictable transparent pricing with zero egress fee surprises, ultra-low latency edge routing, and hands-on managed engineering, while supporting seamless hybrid cloud interconnects with AWS and GCP."
    },
    {
      q: "How do you guarantee high availability and zero-downtime deployments?",
      a: "We architect multi-zone container clusters with automated Kubernetes orchestration, health-checked self-healing pods, blue-green or canary deployment pipelines, and global anycast DNS failover."
    },
    {
      q: "Can you migrate our existing infrastructure without business disruption?",
      a: "Yes. We execute phased migration strategies using live database replication, traffic mirroring, and gradual DNS cutovers to guarantee zero data loss and uninterrupted service for your end users."
    },
    {
      q: "How do you audit and optimize our monthly cloud infrastructure spend?",
      a: "We conduct deep FinOps reviews—right-sizing idle instances, pruning orphaned storage, implementing automated auto-scaling rules, utilizing spot/reserved capacities, and tuning database queries to eliminate wasted spend."
    },
    {
      q: "Do you manage infrastructure via Infrastructure as Code (IaC)?",
      a: "Yes. 100% of cloud resources, networks, firewalls, and Kubernetes manifests are codified using Terraform, OpenTofu, or Ansible, ensuring reproducible, version-controlled environments across staging and production."
    },
    {
      q: "What monitoring, observability, and incident response SLAs do you provide?",
      a: "We deploy unified Prometheus, Grafana, and OpenTelemetry logging stacks with 24/7 synthetic monitoring, automated alert routing, and a guaranteed sub-15-minute response SLA for critical production incidents."
    }
  ],
  "cybersecurity": [
    {
      q: "How does the Neuravolt Guardian self-healing SRE platform operate?",
      a: "Guardian continuously inspects runtime memory, kernel traces, and request payloads. When an anomaly, DDoS vector, or memory leak is detected, it autonomously isolates the container, applies mitigation rules, and restarts healthy instances in real time."
    },
    {
      q: "Do you conduct Vulnerability Assessment and Penetration Testing (VAPT)?",
      a: "Yes. We perform comprehensive black-box, grey-box, and white-box penetration testing across web applications, mobile binaries, APIs, and cloud networks, delivering prioritized remediation roadmaps."
    },
    {
      q: "How do you implement Zero-Trust network architecture for organizations?",
      a: "We enforce continuous verification: identity-aware access proxies, least-privilege role-based access controls (RBAC), mutual TLS (mTLS) across microservices, hardware 2FA keys, and micro-segmented network zones."
    },
    {
      q: "Can you assist with compliance frameworks like SOC 2, ISO 27001, and DPDP?",
      a: "Yes. We conduct gap assessments, implement the required technical and operational security controls, automate compliance evidence gathering, and prepare your systems for successful third-party audits."
    },
    {
      q: "How do you protect applications against sophisticated DDoS and ransomware attacks?",
      a: "We deploy multi-layered perimeter defenses: edge CDN scrubbing, intelligent Web Application Firewalls (WAF) with rate limiting, immutable cloud backups with WORM (Write Once, Read Many) policies, and endpoint detection."
    },
    {
      q: "What happens if a security incident occurs? What is your response protocol?",
      a: "Our 24/7 security team triggers instant automated quarantine protocols, preserves digital forensic evidence, performs root-cause analysis, patches the attack vector, and delivers an exhaustive post-mortem report."
    }
  ],
  "iot-solutions": [
    {
      q: "Which hardware platforms, microcontrollers, and protocols do you support?",
      a: "We work with ESP32, STM32, Raspberry Pi, Nordic nRF, and industrial PLCs across protocols including MQTT, CoAP, Modbus, RS-485, CAN bus, LoRaWAN, BLE, and Zigbee."
    },
    {
      q: "How do you secure connected IoT hardware from unauthorized tampering?",
      a: "We implement hardware root-of-trust, encrypted firmware binaries, mutual TLS (mTLS) for device-to-cloud communications, secure boot sequences, and physically disable local debug interfaces on production hardware."
    },
    {
      q: "How do you deploy remote Over-the-Air (OTA) firmware updates safely?",
      a: "We engineer robust OTA update systems with delta compression, cryptographic checksum validation, dual-bank A/B fallback partitioning, and staged progressive rollouts to prevent device bricking."
    },
    {
      q: "Can you build real-time supervisory dashboards and telemetry visualizations?",
      a: "Yes. We build responsive web and mobile SCADA-style dashboards featuring low-latency WebSocket streaming, historical time-series analytics, geo-fenced asset tracking, and automated SMS/WhatsApp alerts."
    },
    {
      q: "How do edge devices continue functioning when internet connectivity drops?",
      a: "We program edge controllers with local caching, on-device threshold evaluation, and autonomous fail-safe routines, ensuring machinery operates safely during outages and syncs buffered data once connectivity resumes."
    },
    {
      q: "Do you support custom PCB design and industrial enclosure prototyping?",
      a: "Yes. We collaborate with hardware partners for custom schematic design, multi-layer PCB layout, component sourcing, 3D enclosure modeling, and small-batch pilot assembly."
    }
  ],
  "consultancy": [
    {
      q: "What does a Fractional CTO or Technology Consultancy engagement include?",
      a: "A seasoned engineering leader embeds with your executive team to guide technical strategy, evaluate architecture trade-offs, mentor senior developers, conduct vendor audits, and align engineering milestones with your business roadmap."
    },
    {
      q: "When should our company engage a technology consultancy instead of hiring full-time?",
      a: "When you require high-level architectural oversight, an unbiased tech stack audit, cloud cost reduction, or AI roadmap design without incurring the high overhead of a full-time C-suite executive before reaching appropriate scale."
    },
    {
      q: "Do you perform technical due diligence for investors and acquisitions?",
      a: "Yes. We examine codebase maintainability, architectural scalability, infrastructure security, licensing compliance, and technical debt risks to provide comprehensive due diligence reports for venture and M&A transactions."
    },
    {
      q: "How do you structure legacy software modernization without disrupting operations?",
      a: "We perform domain-driven audits, decouple monolithic bottlenecks using the strangler pattern, introduce automated testing safeguards, and incrementally migrate components to modern cloud-native architectures."
    },
    {
      q: "Can you help our enterprise define an actionable AI transformation roadmap?",
      a: "Yes. We audit your operational workflows, identify high-ROI opportunities for automation and machine learning, assess data readiness and compliance requirements, and build prioritized execution roadmaps."
    },
    {
      q: "What engagement models and billing structures do you offer for consultancy?",
      a: "We offer flexible monthly advisory retainers, fixed-scope architecture audits, and dedicated fractional leadership packages tailored to your company's stage and strategic requirements."
    }
  ],
  "erp": [
    {
      q: "Why choose a custom Neuravolt ERP over commercial software like SAP or Oracle?",
      a: "Commercial ERPs enforce rigid workflows and charge hefty per-user subscription fees. A custom ERP is engineered precisely around your proprietary business workflows, integrates with your existing tools, and eliminates recurring per-seat licenses."
    },
    {
      q: "Which business operational modules can be built into the ERP?",
      a: "We build unified modules for financial accounting, multi-warehouse inventory management, procurement and vendor portals, order processing, production scheduling, asset tracking, and executive analytics."
    },
    {
      q: "How do you migrate historical data from spreadsheets and legacy databases?",
      a: "We use automated ETL data pipelines with schema validation, deduplication, and reconciliation scripts, ensuring zero data loss and flawless data fidelity during migration cutover."
    },
    {
      q: "How do you enforce user permissions and security within the ERP?",
      a: "We implement granular role-based access control (RBAC), multi-entity isolation, multi-factor authentication, and immutable audit logs that record every single transaction and record update."
    },
    {
      q: "Can the ERP integrate with external banking APIs, taxation portals, and third-party tools?",
      a: "Yes. We integrate with direct banking payout APIs, automated GST/tax filing portals, payment gateways, and third-party logistics/shipping aggregators."
    },
    {
      q: "What does employee training and deployment onboarding look like?",
      a: "We deploy in phased milestones, provide hands-on role-specific training sessions, deliver recorded video tutorials and documentation, and provide dedicated on-call support during go-live."
    }
  ],
  "hrms": [
    {
      q: "How does the HRMS handle complex shift schedules, attendance, and remote check-ins?",
      a: "Our HRMS supports biometric hardware integrations, mobile GPS geo-fenced check-ins, automated multi-shift rotations, overtime calculations, and real-time leave balance adjustments."
    },
    {
      q: "Does the payroll engine support statutory compliance and automated tax calculations?",
      a: "Yes. It automates PF, ESI, TDS, professional tax, gratuity, and salary slip generation with one-click bank payout batch file exports and compliant year-end tax declaration workflows."
    },
    {
      q: "Can employees access self-service features on mobile and web?",
      a: "Yes. Employees have access to an intuitive self-service portal to request leaves, download salary slips, submit expense reimbursement claims, view team directories, and view company announcements."
    },
    {
      q: "How do you support performance appraisals, OKRs, and employee reviews?",
      a: "The system provides 360-degree feedback modules, custom KPI and OKR tracking, continuous review cycles, and one-on-one manager meeting logs to streamline performance management."
    },
    {
      q: "How is confidential employee and compensation data protected?",
      a: "All personal and payroll data is secured with field-level database encryption, strict access privileges based on managerial hierarchy, and complete audit logging of sensitive record access."
    },
    {
      q: "Can the HRMS integrate with our hiring pipeline and recruitment workflows?",
      a: "Yes. It features an integrated Applicant Tracking System (ATS), interview stage tracking, automated offer letter generation, and single-click onboarding into active employee records."
    }
  ],
  "crm": [
    {
      q: "How does your custom CRM ensure leads are never missed or delayed?",
      a: "It captures leads instantly from website forms, WhatsApp, Facebook ads, and emails, with automated round-robin sales rep distribution, instant notifications, and SLA timers that alert managers if a lead is left uncontacted."
    },
    {
      q: "Can the CRM integrate directly with WhatsApp Business and cloud telephony?",
      a: "Yes. We integrate official WhatsApp Business APIs, cloud telephony (click-to-call, call recording, IVR logging), and email sync directly into the customer's chronological activity timeline."
    },
    {
      q: "Can we build multiple customizable sales pipelines and deal stages?",
      a: "Yes. You can configure drag-and-drop Kanban pipelines for different products or business units, establish automated stage entry requirements, and view weighted deal forecasts."
    },
    {
      q: "What sales analytics and rep performance reports are provided?",
      a: "Interactive executive dashboards display lead velocity, stage conversion ratios, sales rep quotas, deal aging analyses, and customer acquisition costs in real time."
    },
    {
      q: "How easily can we migrate our existing customer data from HubSpot or Salesforce?",
      a: "We provide automated migration scripts that map custom fields, historical communication logs, pipeline stages, and contact associations with complete data integrity."
    },
    {
      q: "Does the CRM include customer support ticketing and post-sale management?",
      a: "Yes. An integrated omnichannel ticketing module converts customer support requests into tickets with SLA tracking, priority escalation, and customer satisfaction tracking."
    }
  ],
  "managed-services": [
    {
      q: "What is included in your Managed Services and Maintenance agreements?",
      a: "We provide 24/7 uptime monitoring, server security patching, database optimization, periodic vulnerability scans, automated backups, and dedicated development hours for ongoing enhancements."
    },
    {
      q: "What are your guaranteed incident response times during unexpected downtime?",
      a: "For critical Tier-1 production incidents, our engineering team initiates active mitigation within 15 minutes, supported by automated pager alerts and 24/7 on-call engineers."
    },
    {
      q: "Can our allocated monthly maintenance hours be used for new feature development?",
      a: "Yes. Monthly retainer hours can be utilized flexibly for UI refinements, performance improvements, third-party API updates, or small feature additions."
    },
    {
      q: "How do you handle data backups and disaster recovery planning?",
      a: "We configure automated daily encrypted database snapshots and weekly full application backups stored across isolated multi-region cloud buckets, with scheduled recovery drills."
    },
    {
      q: "Can you take over maintenance of software built by another vendor or internal team?",
      a: "Yes. We conduct an initial code and architecture audit, remediate critical security vulnerabilities, document deployment pipelines, and take over ongoing operational stewardship."
    },
    {
      q: "Do we receive regular transparency reports on system health and work done?",
      a: "Yes. You receive comprehensive monthly reports outlining uptime percentages, incident logs, security patches applied, and actionable recommendations for performance optimization."
    }
  ]
};

// Aliases for related service slugs
SERVICE_FAQS["ai-development"] = SERVICE_FAQS["ai-solutions"];
SERVICE_FAQS["ai-automation"] = SERVICE_FAQS["ai-solutions"];
SERVICE_FAQS["custom-software"] = SERVICE_FAQS["software-development"];
SERVICE_FAQS["cloud-devops"] = SERVICE_FAQS["cloud-infrastructure"];
SERVICE_FAQS["iot"] = SERVICE_FAQS["iot-solutions"];

export const getServiceFaqs = (slug) => {
  if (!slug) return FAQS;
  return SERVICE_FAQS[slug] || FAQS;
};


export const ABOUT_VALUES = [
  { title: "Craft", desc: "We sweat the details others skip." },
  { title: "Velocity", desc: "We ship fast without breaking trust." },
  { title: "Ownership", desc: "Your outcomes are our outcomes." },
  { title: "Transparency", desc: "Honest updates, always." },
];

export const TIMELINE = [
  { year: "2023", title: "A Vision", body: "Neuravolt was founded with the vision of building intelligent software and AI powered digital products for startups and businesses." },
  { year: "2024", title: "Growth", body: "Expanded into web development, mobile applications, AI, cloud infrastructure, and delivered solutions across multiple industries." },
  { year: "2025", title: "Momentum", body: "Scaled into a full-service software engineering company with ERP, HRMS, CRM, IoT, enterprise software and automation solutions." },
  { year: "2026", title: "The Evolution", body: "Officially incorporated as VOLTNOVATE SOLUTIONS LLP and recognized as a Startup India (DPIIT) startup, positioning Neuravolt as an AI first software engineering company serving clients globally." },
];

export const SERVICE_OPTIONS = ALL_OFFERINGS.map((s) => s.title);
export const BUDGET_OPTIONS = ["< $10k", "$10k - $25k", "$25k - $50k", "$50k - $100k", "$100k+"];
export const PRIMARY_EMAIL = "info@neuravolt.in";
export const PRIMARY_PHONE = "+91 9202243725";
export const CORPORATE_ADDRESS = "";
export const CORPORATE_ADDRESS_2 = "";
export const REGISTERED_ADDRESS = "House No 2, Sai Dham Colony, DD Nagar, Gwalior, Madhya Pradesh - 474005, India";
export const LEGAL_ENTITY = "VOLTNOVATE SOLUTIONS LLP";

/* ---------------- Corporate Identity & Strategic Positioning ---------------- */
export const TAGLINE = "Bringing Intelligent Solutions for Measurable Impact";

export const COMPANY_PURPOSE = "To bring fragmented technology together into one connected ecosystem.";

export const COMPANY_VISION = "To become a global technology company where organizations can build, operate, and grow through one integrated ecosystem.";

export const COMPANY_MISSION = "To continuously innovate, build, and integrate the technologies, infrastructure, platforms, and capabilities that organizations need to operate in a connected digital world.";

export const COMPANY_CORE_THOUGHT = "Technology is becoming more powerful, but increasingly fragmented. NeuraVolt exists to bring it together into one integrated ecosystem, continuously evolving through innovation.";

/* ---------------- Official Social Links ---------------- */
export const SOCIAL_LINKS = {
  linkedin: "https://www.linkedin.com/company/neuravolt-technology/posts/?feedView=all",
  linkedin_profile: "https://linkedin.com/in/neuravolt-technologies/",
  instagram: "https://instagram.com/we.neuravolt",
  github: "https://github.com/neuravolt-technologies",
  google_maps: "https://www.google.com/maps/place/Neuravolt+Technology/data=!4m2!3m1!1s0x0:0x47b13dd0dbd4c8f5"
};
